import { create } from 'zustand';
import type { BoardState, DrawnOpeningInfo, Move, TimeControlPreset, TimeControlKey, ClockSyncEvent, GameClockInit, ChallengeSettings } from '@draughts/shared';
import { Color, GameStatus, GameVariant, toGameVariant, getVariantString, MOVE_REJECTED, CHAT_RATE_LIMITED, CHAT_ROOM_CLOSED, RESIGN_ALREADY_ENDED, REASON_NO_FIRST_MOVE, TIME_CONTROL_PRESETS, MAX_OFFERTE_PATTA_PER_PARTITA } from '@draughts/shared';
import { applyMove } from '@draughts/core';
// dashboard-cache.ts has no runtime imports of its own, so this does not pull
// the lazy DashboardPage chunk into the game bundle.
import { markDashboardStale } from '../components/dashboard/dashboard-cache';
import { mostraFestaGiornata } from './festa-giornata';
import { usePreferencesStore } from './preferences-store';
import { useArenaStore } from './arena-store';
import { useChatStore } from './chat-store';
import { useSpectatorsStore } from './spectators-store';
import {
  initSocket,
  sendMove,
  resignGame,
  disconnectSocket,
  isConnected,
  setSocketAuthToken,
  quickPlay,
  createLobbyTable,
  sitAtTable,
  cancelLobbyTable,
  listLobby,
  LobbyRequestError,
  checkActiveGame,
  risvegliaSocket,
  getSocket,
  requestRematch as socketRequestRematch,
  declineRematch as socketDeclineRematch,
  setMatchReady as socketSetMatchReady,
  offerDraw as socketOfferDraw,
  acceptDraw as socketAcceptDraw,
  declineDraw as socketDeclineDraw,
  requestTakeback as socketRequestTakeback,
  acceptTakeback as socketAcceptTakeback,
  declineTakeback as socketDeclineTakeback,
  acceptChallenge as socketAcceptChallenge,
  declineChallenge as socketDeclineChallenge,
  cancelChallenge as socketCancelChallenge,
  wasEvictedByNewSession,
  type GameRatings,
  type CheckActiveGameResponse,
  type RispostaPatta,
} from '../services/socket';
import { annuncioApertura, aperturaStabile } from '../utils/opening-labels';
import { useAuthStore } from './auth-store';
import { useGameStore } from './game-store';
import { useTournamentStore, callbackDellaStanzaTorneo } from './tournament-store';
import { useOnlineStore, type OnlineUser } from './online-store';
import { useLobbyStore } from './lobby-store';
import { tournamentErrorKeyFor } from '../utils/tournament-error-key';
import { CHIAVI_ERRORE_ONLINE } from '../utils/chiave-errore-online';
import { chiaveRifiutoPatta } from '../utils/chiave-rifiuto-patta';
import { sbloccoPattaDopoMossa } from '../utils/sblocco-patta';
import { log } from '../utils/logger';
import { SOGLIA_ECO_MS, ATTESA_INVALIDAZIONE_PRESENZA_MS } from '../constants/timing';
import { breadcrumbDesync, segnalaDesync } from '../utils/diagnosi-sincronia';
import { playMoveSound, playSound } from '../utils/audio';
import { vibrate } from '../utils/vibration';
import {
  trackGameTimeout,
  trackGameAbandoned,
  trackGameReconnected,
  trackRematchAccepted,
  trackRematchDeclined,
  trackGameStarted,
  trackFirstGameEver,
  trackVariantBreadth,
} from '../utils/analytics';

export type MultiplayerStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'creating'
  | 'waiting'      // Created room, waiting for opponent
  | 'joining'
  | 'searching'    // Searching for match via matchmaking
  | 'playing'
  | 'ended';

/** Clock state for the multiplayer store */
export interface MultiplayerClockState {
  /** Remaining time for white in milliseconds */
  white: number;
  /** Remaining time for black in milliseconds */
  black: number;
  /** Which player's clock is currently running */
  activePlayer: 'white' | 'black' | null;
  /** Timestamp of the last server sync */
  lastSyncAt: number | null;
  /** Increment per move in milliseconds */
  increment: number;
}

interface MultiplayerStore {
  // Connection state
  status: MultiplayerStatus;
  /**
   * La presenza (tavoli di lobby + lista dei collegati) non descrive piu' niente
   * di vivo, e chi la disegna deve dirlo invece di mostrarla.
   *
   * E' separata da `status` perche' risponde a una domanda diversa. `status`
   * dice com'e' il socket ADESSO, e sfarfalla: passa per `disconnected` anche
   * per un singhiozzo da un secondo. Questa dice «quelle liste sono scadute», e
   * si alza solo quando la caduta e' durata abbastanza
   * (`ATTESA_INVALIDAZIONE_PRESENZA_MS`) oppure subito, quando il dispositivo
   * DICHIARA di non avere rete (`useScadenzaPresenza`). Un componente che
   * guardasse `status === 'disconnected'` svuoterebbe le liste a ogni blip.
   *
   * Parte `false`: all'avvio le liste sono vuote perche' non sono ancora
   * arrivate, non perche' sono morte, e sono due stati da non confondere.
   */
  presenzaScaduta: boolean;
  roomId: string | null;
  playerColor: 'white' | 'black' | null;
  variant: GameVariant;
  error: string | null;
  /**
   * La partita e' stata RILASCIATA dal rientro, e non si e' potuto sapere
   * com'e' finita: il server non la conosce piu' (stanza raccolta dopo una
   * lunga assenza). Serve a dire perche' si torna alla home, invece di farlo
   * in silenzio. Lo consuma chi mostra l'avviso.
   */
  partitaTerminataMentreEriVia: boolean;

  // Game state (synced from server)
  boardState: BoardState | null;
  // Authoritative board snapshot taken right before an optimistic local move,
  // used to roll back if the server rejects that move (game:error). Cleared
  // once the server confirms the move via game:update. Null when no optimistic
  // move is awaiting confirmation.
  preMoveBoardState: BoardState | null;
  /**
   * Quando la mossa ottimistica e' stata spedita. Serve a UNA cosa sola: dire
   * per quanto tempo ancora ha senso non calpestarla (vedi `SOGLIA_ECO_MS`).
   *
   * Si azzera SEMPRE insieme a `preMoveBoardState`, nello stesso `set`. In
   * teoria basterebbe scriverlo in `makeMove`, visto che lo si legge solo a
   * damiera ottimistica presente — ma «in teoria non e' raggiungibile» e' il
   * genere di ragionamento che lascia un valore vecchio in giro e rende i test
   * dipendenti dall'ordine. Le due cose sono una cosa sola: si spengono insieme.
   */
  preMoveSentAt: number | null;
  lastMove: Move | null;
  gameResult: 'white' | 'black' | 'draw' | null;
  gameEndReason: string | null;
  // Wall-clock ms when the current online game began (set in onGameStart); used
  // to compute real game duration for analytics. Null when no game is active.
  gameStartedAtMs: number | null;
  /**
   * Istante (epoch ms) entro cui va giocata la PRIMA mossa vera, o null se non
   * c'è scadenza — in torneo, dove l'orologio parte subito, o quando il server
   * non l'ha mandata.
   *
   * Si calcola alla RICEZIONE (`Date.now() + restaMs`) e non si spegne a mano
   * quando la mossa arriva: chi lo mostra deriva la visibilità dalla damiera
   * (`moveHistory.length <= openingPlyCount`), che è la stessa condizione che
   * usa il server. Un flag da spegnere avrebbe tre cicli di vita da tenere
   * allineati — delega all'arena, delega al torneo, mossa ottimistica locale.
   */
  primaMossaScadeAt: number | null;
  /** Cosa succede alla scadenza: annullamento, o sconfitta a tavolino in arena. */
  primaMossaEsito: 'void' | 'forfeit' | null;
  /**
   * Quante semi-mosse in `boardState.moveHistory` le ha messe l'apertura: non
   * sono mosse di nessuno dei due giocatori. È il gemello di
   * `arena-store.openingPlyCount` e di `GameRoom.getPlayedMoveCount()`.
   *
   * Il CONTO non guarda il modo dell'apertura — si ricava da `opening.moves`
   * anche quando il modo è `'free'` — mentre il CARTELLINO
   * (`drawnOpening`) è solo per il sorteggio. Stessa distinzione, e stessa
   * ragione, di `arena-store.setGameStart`: legare il conto al modo lo
   * romperebbe il giorno in cui il server applicasse semi-mosse di un altro
   * tipo, e il conto della prima mossa sparirebbe da una partita appena aperta.
   */
  openingPlyCount: number;
  /**
   * La posizione da cui questa partita e' COMINCIATA, quando non e' l'apertura
   * standard: una sfida nata dall'editor (diretta, da link, o seduti a un
   * tavolo di lobby con posizione). `null` per ogni altra partita.
   *
   * Vive qui e non solo in `game-store` perche' questo e' l'unico store che la
   * riceve, e perche' `game-store` non puo' leggere questo (e' questo a
   * importare quello: due `create()` in ciclo si vedrebbero `undefined`). Chi
   * monta la schermata online la passa a `enterOnlineGame` e la riapplica con
   * `setOnlineStartBoard` quando cambia.
   *
   * NON e' appiccicosa, al contrario dei campi qui intorno: si scrive sempre
   * `?? null`. Un server vecchio che non la manda vale `null`, cioe' il
   * comportamento di prima; tenerla addosso alla partita DOPO significherebbe
   * invece far lanciare `applyMove` dentro un updater di zustand — l'incidente
   * che `enterOnlineGame` esiste per riparare.
   */
  startBoard: BoardState | null;

  // Clock state
  clockState: MultiplayerClockState | null;
  timeControl: TimeControlKey | null;
  // Whether the current game is rated (server-authoritative, from game:start).
  // Drives the header's Rated/Casual pill. Set unconditionally on every
  // game:start and deliberately preserved across reset() so a casual rematch
  // survives a leave/rejoin. Defaults to true (rated) when the server omits it.
  isRated: boolean;

  // Waiting-at-a-table state. `searchTimeControl` is null for a custom table,
  // whose cadence need not be one of the quick presets.
  searchTimeControl: TimeControlPreset | null;
  searchStartTime: number | null;
  queuePosition: number | null;
  estimatedWait: number | null;
  opponent: { username: string; rating?: number | null; avatar?: string | null; country?: string | null } | null;
  /*
   * Chiave i18n dell'avviso su ciò che è accaduto alla coda: la partita annullata
   * perché l'avversario è uscito prima della prima mossa, o il tavolo scaduto
   * dopo i suoi 10 minuti.
   *
   * Lo legge `AvvisoLobby`, montato in `App` accanto a `QueuedTableBanner`. Il
   * campo esisteva già ma NESSUN componente lo leggeva: la frase, tradotta in
   * otto lingue, non arrivava a schermo.
   */
  abortNotice: string | null;
  // Drawn opening ("apertura sorteggiata") for the current game, set on
  // game:start / rematch:start when the challenge used a drawn opening; drives
  // the transient in-game banner. Null for free openings.
  drawnOpening: DrawnOpeningInfo | null;

  // Chat state and actions live in chat-store: they are the same in every mode,
  // while this store is bypassed entirely by arena and tournament games.

  // Rematch state
  sessionScore: { white: number; black: number };
  opponentWantsRematch: boolean;
  isRequestingRematch: boolean;
  canRematch: boolean;
  /** Server deadline (epoch ms) for the rematch offer THIS player sent. */
  rematchOfferExpiresAt: number | null;
  /**
   * Why the pending rematch ended, once it has. 'declined' and 'expired' are
   * NOT interchangeable: after a refusal the server deletes the room and there
   * is nothing left to offer, after a timeout the room survives and the player
   * may try again — so `canRematch` follows this, and so does the wording.
   */
  rematchNotice: 'declined' | 'expired' | null;

  // Two-leg match state ("andata e ritorno"): a direct challenge with a drawn
  // opening is played twice, same opening, swapped colours. Null/1 for the
  // single games every other flow produces.
  matchLegIndex: number | null;
  matchTotalLegs: number | null;
  /** Match points from this player's point of view (1 win / 0.5 draw). */
  matchScore: { you: number; opponent: number } | null;
  matchComplete: boolean;
  /** Epoch ms at which the return leg auto-starts; null when none is pending. */
  returnLegStartsAt: number | null;
  returnLegReady: boolean;
  /** userIds who already pressed "pronto" for the return leg. */
  matchReadyUserIds: string[];

  // Draw offer state
  drawOffered: boolean;
  opponentOfferedDraw: boolean;
  /** Transient "the opponent refused your draw" banner (offerer only). */
  drawDeclinedNotice: boolean;
  /**
   * Quante proposte di patta mi restano in QUESTA partita, e se devo muovere
   * prima di riproporre. Il freno vero sta sul server (`offertePatta` in
   * `game/room.ts`): questi due campi sono la sua copia, per poter spegnere il
   * bottone e dire perche' invece di far premere a vuoto.
   *
   * Si riempiono dall'ack dell'offerta e dal payload di rientro; lo sblocco il
   * client lo deriva dalla propria mossa (`utils/sblocco-patta.ts`). Ogni
   * incertezza si risolve verso «acceso»: un bottone murato e' il difetto che
   * il commit 4d09615b ha dovuto riparare, un gesto sprecato no.
   */
  drawOffersLeft: number;
  drawMustMove: boolean;

  // Takeback state (casual online only)
  takebackOffered: boolean;            // my request is pending
  opponentRequestedTakeback: boolean;  // opponent asked me
  pendingTakebackPlies: number | null; // number of plies to undo (for the banner text)
  pendingTakebackToPly: number | null; // target ply (informational)
  takebackDeclinedNotice: boolean;     // transient "your request was declined"
  takebackAcceptedNotice: boolean;     // transient "your request was accepted" (requester only)

  // Disconnection state
  isOpponentDisconnected: boolean;
  opponentDisconnectedColor: 'white' | 'black' | null;
  graceTimeRemaining: number | null; // in ms
  /**
   * L'avversario non si e' mai presentato (torneo): la scheda dice «Non
   * presente» invece di «Disconnesso». Letto solo con `isOpponentDisconnected`.
   */
  opponentMaiArrivato: boolean;
  /**
   * Nessun forfait allo scadere della grazia: se non arriva o non rientra, si
   * vince quando scade il SUO orologio (torneo, prima della sua prima mossa).
   * Letto solo con `isOpponentDisconnected`.
   */
  opponentDecideLOrologio: boolean;

  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  /**
   * Dichiara scaduta la presenza: alza la bandiera e svuota i due store che
   * vivono di quello che il server presta. Punto UNICO — nessun componente
   * invalida da se'.
   */
  invalidaPresenza: () => void;
  createQuickTable: (variant: string, timeControl: TimeControlPreset) => Promise<void>;
  createCustomTable: (settings: ChallengeSettings) => Promise<boolean>;
  sitAtLobbyTable: (tableId: string) => Promise<boolean>;
  cancelMyTable: () => void;
  /** Toglie l'avviso: lo fa la ✕, e il timer di `AvvisoLobby`. */
  clearAbortNotice: () => void;
  makeMove: (move: Move) => void;
  resign: () => void;
  reset: () => void;
  /**
   * Richiede al server lo stato vero della partita e lo applica.
   *
   * A socket giu' NON interroga: `emettiCheckActive` risponderebbe
   * `{hasActiveGame:false}` senza `timedOut` — cioe' «non hai partite» — e lo
   * store rilascerebbe una partita viva. Si sveglia il socket, e il resync lo
   * fa `onConnect`.
   */
  resyncGame: () => Promise<void>;
  /** Spegne l'errore mostrato a schermo. */
  clearError: () => void;
  /** L'avviso di rientro tardivo e' stato mostrato. */
  clearPartitaTerminataMentreEriVia: () => void;
  setVariant: (variant: GameVariant) => void;

  // Draw offer actions
  offerDraw: () => void;
  acceptDraw: () => void;
  declineDraw: () => void;

  // Takeback actions (casual online only)
  requestTakeback: (toPly: number) => void;
  acceptTakeback: () => void;
  declineTakeback: () => void;

  // Rematch actions
  requestRematch: () => Promise<void>;
  declineRematch: () => Promise<void>;

  // Two-leg match actions
  setReturnLegReady: () => void;

  // Clock actions
  initClock: (clockInit: GameClockInit) => void;
  syncClock: (sync: ClockSyncEvent) => void;
  tickClock: () => void;

  // Internal setters
  setStatus: (status: MultiplayerStatus) => void;
  setError: (error: string | null) => void;
  setBoardState: (state: BoardState) => void;
  setGameEnd: (result: 'white' | 'black' | 'draw', reason: string) => void;
}

// Refresh the access token before connecting only when it is close to expiring.
// Access tokens last ~15min; refreshing on EVERY connect turned socket-reconnect
// churn into a POST /refresh storm that tripped the rate limiter. We decode the JWT
// `exp` and skip the refresh while the token is still comfortably valid. An
// unreadable/exp-less token is treated as "refresh" (conservative).
const TOKEN_REFRESH_SKEW_MS = 120_000;
function accessTokenExpiresSoon(token: string): boolean {
  try {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) return true;
    const payload = JSON.parse(atob(payloadB64)) as { exp?: number };
    if (typeof payload.exp !== 'number') return true;
    return payload.exp * 1000 - Date.now() <= TOKEN_REFRESH_SKEW_MS;
  } catch {
    return true;
  }
}

/**
 * Apply a game:check-active result on (re)connect. Three cases:
 *  - hasActiveGame: rejoin the live game (arena games delegate to the arena store).
 *  - endedGame: the game ended while we were away (e.g. a disconnection forfeit
 *    that never delivered game:end) — show game-over + rematch, not a frozen board.
 *  - neither, but we're still holding a live game whose room vanished: clear the
 *    stale board so the player is never stranded on a game that can't resolve.
 * Shared by both onConnect and the already-connected reconnect path.
 */
/** L'offerta pendente è NOSTRA? (`null` = nessuna offerta.) */
function nostraOfferta(offerente: 'WHITE' | 'BLACK' | null | undefined, mio: 'white' | 'black'): boolean {
  return offerente != null && offerente.toLowerCase() === mio;
}

/** L'offerta pendente è dell'AVVERSARIO? */
function offertaAvversaria(offerente: 'WHITE' | 'BLACK' | null | undefined, mio: 'white' | 'black'): boolean {
  return offerente != null && offerente.toLowerCase() !== mio;
}

/** Di chi e' un evento di partita, quando il filo lo dice. */
type Destinatario = 'multiplayer' | 'arena' | 'torneo' | 'nessuno';

/**
 * A chi appartiene la stanza di questo evento.
 *
 * `null` vuol dire «il filo non lo dice» — un server piu' vecchio del deploy in
 * corso — e solo allora si ricade sull'euristica di prima, quella che guarda lo
 * stato degli altri store. Che e' esattamente cio' che ha prodotto il difetto:
 * a chi aveva un'arena aperta (anche solo la lobby di una mai cominciata, anche
 * solo un podio rimasto indietro) le mosse delle PROPRIE sfide venivano buttate
 * in silenzio, mentre `game:clock-sync` — che quella guardia non ce l'ha —
 * continuava ad arrivare: damiera ferma, orologio che scorre.
 *
 * L'ordine e' voluto: prima le competizioni, che hanno una stanza sola e
 * nominata, poi il multiplayer, il cui `roomId` puo' essere quello di una
 * partita gia' finita e lasciata li'.
 */
function destinatarioPerStanza(roomId: string | undefined, roomIdMultiplayer: string | null): Destinatario | null {
  if (!roomId) return null;
  if (roomId === useArenaStore.getState().currentRoomId) return 'arena';
  if (roomId === useTournamentStore.getState().currentRoomId) return 'torneo';
  if (roomId === roomIdMultiplayer) return 'multiplayer';
  return 'nessuno';
}

/**
 * Rimettere la lobby in pari col server.
 *
 * Il tavolo aperto di chi si scollega viene ritirato dal server
 * (`handleLobbyDisconnect`) e annunciato a chi era collegato IN QUEL MOMENTO:
 * chi era caduto quell'annuncio non lo riceve mai. Fino a ieri nessuno glielo
 * diceva piu' — `setSnapshot` aveva un solo chiamante, l'apertura del pannello
 * «Gioca» — e al rientro restavano appesi il banner «Sei in coda» e il suo
 * cronometro, su un tavolo che lato server non esisteva piu'.
 *
 * Lo snapshot e' autorevole anche sul PROPRIO tavolo (`myTableId`), quindi
 * questa singola domanda ripara sia la lista sia l'attesa. Silenziosa di
 * proposito: e' una riparazione di sfondo, non un'azione dell'utente.
 */
function risincronizzaLobby(): void {
  listLobby()
    .then((snapshot) => {
      useLobbyStore.getState().setSnapshot(snapshot.tables, snapshot.myTableId);
      /*
       * E i CONTATORI, che lo snapshot porta da sempre (`LobbySnapshot.counts`)
       * e che qui venivano buttati via: l'unico a usarli era l'apertura del
       * pannello «Gioca».
       *
       * Finche' i contatori non scadevano mai non si notava. Da quando
       * `invalidaPresenza` li azzera, questa e' la riga che li rimette: senza,
       * dopo un rientro la pillola «N Online» della hero resterebbe a zero fino
       * al prossimo `online:counts` casuale — che arriva quando QUALCUN ALTRO si
       * collega o si scollega, cioe' quando capita.
       */
      useOnlineStore.getState().setCounts(snapshot.counts);
    })
    .catch((err) => {
      log.warn('Multiplayer', 'risincronizzazione lobby fallita', { err });
    });
}

/**
 * Il timer che trasforma «il socket e' caduto» in «quelle liste sono scadute».
 *
 * Vive a livello di modulo e non nello stato: e' un dettaglio di attuazione, e
 * nessuno lo disegna. Uno solo per volta — chi lo arma disarma il precedente.
 */
let timerScadenzaPresenza: ReturnType<typeof setTimeout> | null = null;

function fermaTimerScadenzaPresenza(): void {
  if (timerScadenzaPresenza === null) return;
  clearTimeout(timerScadenzaPresenza);
  timerScadenzaPresenza = null;
}

/**
 * Cosa fare della risposta ad Accetta / Rifiuta la patta.
 *
 * L'unico caso che conta e' la partita gia' finita sul server — chi aveva
 * proposto ha abbandonato un attimo prima e qui la fine non e' arrivata: il
 * server manda l'esito (come per l'abbandono) e lo si disegna, invece di
 * lasciare una barra che non risponde. Gli altri rifiuti (offerta ritirata da
 * una mossa, per esempio) si chiudono gia' da soli con `game:draw-declined`.
 */
function rispostaAllaPatta(
  roomId: string,
  risposta: RispostaPatta,
  set: (partial: Partial<MultiplayerStore>) => void,
  get: () => MultiplayerStore,
): void {
  if (get().roomId !== roomId) return;
  if (risposta.error !== RESIGN_ALREADY_ENDED) return;
  log.info('Multiplayer', 'patta su partita gia\' finita: applico l\'esito', { roomId, haEsito: !!risposta.ended });
  set({ opponentOfferedDraw: false, drawOffered: false });
  if (risposta.ended) {
    applyReconnectResult(risposta.ended, set, get);
  } else {
    void get().resyncGame();
  }
}

/**
 * Il server NON ha guardato: la domanda non e' arrivata, o la risposta non e'
 * tornata.
 *
 * `checkActiveGame` risponde `{ hasActiveGame: false }` in situazioni che non
 * sono la stessa cosa, e il rilascio della partita le trattava uguali:
 *
 *  - il «no» AUTOREVOLE: il server ha guardato `activeGamesMap`, le stanze
 *    chiuse e il database, e quella partita non c'e' piu' (riavvio, deploy: le
 *    stanze vivono in RAM). Si rilascia, e si e' sempre fatto bene;
 *  - `timedOut`: nessun ack entro `ATTESA_ACK_CHECK_ACTIVE_MS`, due volte di
 *    fila, su un droplet da 1 vCPU e con una sonda che parte anche a ogni
 *    ritorno in primo piano — piu' il silenzio deliberato del server oltre le
 *    venti richieste al minuto, che arriva qui nella stessa forma. Qui nessuno
 *    ha guardato niente: la partita puo' essere VIVA, e `rilasciaPartita`
 *    azzerava avversario, stanza, damiera e orologio. Per la damiera online il
 *    conto tornava comunque (il rilascio porta alla home), ma per quella di
 *    TORNEO un'uscita non esiste — difetto G6,
 *    `docs/AUDIT-TORNEI-DATI-2026-09-24.md` — quindi si perdeva tutto senza
 *    guadagnare niente, e il sintomo visibile era l'avversario «Anonimo» col
 *    punteggio a trattino.
 *
 * ⚠️ `unauthenticated` NON e' in questo elenco, e la differenza e' nel codice
 * di `checkActiveGame`: quando quella risposta arriva fin qui, il rinnovo del
 * token e l'attesa dell'annuncio d'identita' sono GIA' stati tentati e falliti
 * la' dentro. Non e' «non ho guardato», e' «non so chi sei e non riesco a
 * scoprirlo»: richiedere sarebbe un terzo colpo con la stessa identita' rotta,
 * mentre il giocatore resta su una damiera dove non puo' ne' muovere ne'
 * abbandonare. Si rilascia, come prima — c'e' una prova che lo vincola
 * (`multiplayer-store.test.ts`, «non lascia il giocatore in un vicolo cieco»).
 */
function ilServerNonHaGuardato(result: CheckActiveGameResponse): boolean {
  return result.timedOut === true;
}

/** Quanto si aspetta prima di richiedere, dopo una risposta che non ha guardato. */
const ATTESA_RISONDA_MS = 2_000;

/**
 * La ripetizione e' UNA SOLA, e questo e' il suo fermo.
 *
 * Senza, due risposte «non ho potuto guardare» di fila si richiamerebbero a
 * vicenda per sempre su un server irraggiungibile — che e' esattamente la
 * situazione in cui insistere lo peggiora. Si azzera appena una risposta
 * autorevole arriva (di qualunque segno): la prossima caduta ha diritto al suo
 * tentativo.
 */
let sondaGiaRipetuta = false;
let timerRisonda: ReturnType<typeof setTimeout> | null = null;

function risondaPiuTardi(
  set: (partial: Partial<MultiplayerStore>) => void,
  get: () => MultiplayerStore,
): void {
  if (timerRisonda) clearTimeout(timerRisonda);
  sondaGiaRipetuta = true;
  timerRisonda = setTimeout(() => {
    timerRisonda = null;
    void checkActiveGame()
      .then((risposta) => applyReconnectResult(risposta, set, get))
      .catch((err) => log.error('Multiplayer', 'risonda del rientro fallita', err));
  }, ATTESA_RISONDA_MS);
}

function applyReconnectResult(
  result: CheckActiveGameResponse,
  set: (partial: Partial<MultiplayerStore>) => void,
  get: () => MultiplayerStore,
): void {
  /* Una risposta AUTOREVOLE — di qualunque segno — chiude la partita aperta
     dalla ripetizione: la prossima caduta ha diritto al suo tentativo, e il
     timer eventualmente in volo non serve piu' a niente. */
  if (!ilServerNonHaGuardato(result)) {
    sondaGiaRipetuta = false;
    if (timerRisonda) {
      clearTimeout(timerRisonda);
      timerRisonda = null;
    }
  }
  if (result.hasActiveGame && result.roomId && result.state && result.color) {
    /*
     * Una mossa ottimistica IN VOLO sulla stessa stanza non si calpesta.
     *
     * Da quando il risveglio in primo piano consegna la sua risposta (e non la
     * butta piu' via), questo ramo puo' girare su una partita che era ed e'
     * rimasta viva: se proprio in quell'istante una mossa e' partita e non e'
     * ancora tornata l'eco del server, riscrivere `boardState` col vecchio
     * stato farebbe tornare indietro il pezzo per poi rimetterlo avanti un
     * attimo dopo. Non c'e' niente da riallineare — chi conferma o rifiuta e'
     * `onGameUpdate` / `onGameError`, che stanno gia' per arrivare.
     *
     * IN VOLO, pero', e non «mai tornata»: oltre `SOGLIA_ECO_MS` l'eco non sta
     * arrivando, si e' persa — ed e' esattamente il caso in cui questo rientro
     * e' l'unica riparazione possibile. Da li' in poi comanda il server.
     */
    const sentAt = get().preMoveSentAt;
    const ecoAncoraPlausibile = sentAt !== null && Date.now() - sentAt < SOGLIA_ECO_MS;
    if (
      result.roomId === get().roomId &&
      get().status === 'playing' &&
      get().preMoveBoardState !== null &&
      ecoAncoraPlausibile
    ) {
      log.info('Multiplayer', 'rientro: mossa ottimistica in volo, non riscrivo la damiera', {
        roomId: result.roomId,
      });
      return;
    }
    if (get().preMoveBoardState !== null) {
      /* Ci si riallinea NONOSTANTE una mossa ottimistica appesa: l'eco non e'
         mai arrivata. E' il difetto per cui la damiera restava ferma con
         l'orologio che correva, quindi lo si dice anche in produzione. */
      log.warn('Multiplayer', 'rientro: eco mai arrivata, riallineo sullo stato del server', {
        roomId: result.roomId,
        daMs: sentAt !== null ? Date.now() - sentAt : null,
      });
      segnalaDesync('rientro-senza-eco', { roomId: result.roomId, daMs: sentAt !== null ? Date.now() - sentAt : null });
    }
    // Stesso invariante di onGameStart, porta diversa: questo percorso mette
    // status:'playing' senza passarci. Senza questa riga, ricaricare durante una
    // partita con una sfida-link ancora viva rimette l'overlay sopra la
    // scacchiera. Prima della delega arena, così copre anche il rientro in arena.
    useOnlineStore.getState().clearLinkChallenges();
    // Stesso invariante di `onGameStart`: chi e' dentro una partita non e' in
    // coda. Questa e' la porta da cui si rientra dopo un blip di rete, cioe'
    // proprio il caso in cui l'annuncio del ritiro del tavolo si e' perso.
    useLobbyStore.getState().setMyTable(null);
    // Delegate to tournament-store if this is a tournament game.
    //
    // `connection.handler` manda `tournamentId` accanto ad `arenaId` da sempre;
    // finora solo l'arena aveva il suo ramo, e le partite di torneo finivano
    // qui — sulla schermata online normale, senza intestazione del torneo, senza
    // punti, con la chat persa e senza ritorno alla lobby a fine partita.
    if (result.tournamentId && result.roomId && result.state && result.color) {
      useTournamentStore.getState().rientraInPartita({
        tournamentId: result.tournamentId,
        roomId: result.roomId,
        state: result.state,
        color: result.color,
        clock: result.clock ?? null,
        /* `?? null` e non `?? 0`: il numero, quando c'e', e' quello vero della
           variante e della cadenza della stanza; quando non c'e', non si
           inventa. Lo 0 che stava qui e' ESATTAMENTE lo zero che compariva
           accanto al nome dell'avversario di torneo — e non serviva un
           ricarico, bastava tornare in primo piano. */
        opponent: result.opponent?.oddsUserName
          ? {
              id: result.opponent.oddsUserId ?? '',
              username: result.opponent.oddsUserName,
              rating: result.opponent.oddsRating ?? null,
              avatar: result.opponent.oddsAvatar ?? null,
            }
          : null,
        chat: result.chat,
        timeControl: result.timeControl,
        /* L'apertura sorteggiata: senza, il torneo perdeva intestazione e grigi in Mosse. */
        opening: result.opening,
        /* La manche: vedi `campoManche` sul server, e `rientraInPartita`. */
        manche: result.manche,
        /*
         * Le NEGOZIAZIONI pendenti e il freno della patta.
         *
         * Questa lista di campi e' esplicita, e `drawOfferBy`/`takebackOffer`
         * non c'erano: il ramo multiplayer qui sotto le legge da sempre, queste
         * due porte no. Quindi in torneo e in arena un ricarico perdeva la
         * proposta pendente — il bottone mentiva a entrambi, esattamente il
         * difetto che si era riparato per il multiplayer.
         */
        drawOfferBy: result.drawOfferBy,
        takebackOffer: result.takebackOffer,
        offertePatta: result.offertePatta,
      });
      return;
    }

    // Delegate to arena-store if this is an arena game
    if (result.arenaId) {
      useArenaStore.getState().rejoinGame({
        arenaId: result.arenaId,
        roomId: result.roomId,
        state: result.state,
        color: result.color,
        clock: result.clock ?? null,
        /* La cadenza serve all'orologio dell'arena: `clock` non la porta. */
        timeControl: result.timeControl ?? null,
        opponent: result.opponent?.oddsUserName ? {
          id: result.opponent.oddsUserId,
          username: result.opponent.oddsUserName,
          rating: result.opponent.oddsRating,
          avatar: result.opponent.oddsAvatar ?? null,
        } : null,
        chat: result.chat,
        /* Apertura e boost: il rientro li porta, e il bottone del boost non
           deve piu' indovinarli. Vedi `rejoinGame` in arena-store. */
        openingPlyCount: result.openingPlyCount,
        opening: result.opening,
        /* La manche: vedi `campoManche` sul server, e `rejoinGame`. */
        manche: result.manche,
        boosted: result.boosted,
        /* Per la stessa ragione: senza, il conto della prima mossa ripartirebbe
           pieno mentre sul server il tempo è già corso. */
        primaMossa: result.primaMossa,
        /* Negoziazioni pendenti e freno della patta: vedi la nota gemella nella
           delega al torneo qui sopra. Questa lista non le portava. */
        drawOfferBy: result.drawOfferBy,
        takebackOffer: result.takebackOffer,
        offertePatta: result.offertePatta,
      });
      return;
    }
    set({
      status: 'playing',
      roomId: result.roomId,
      boardState: result.state,
      preMoveBoardState: null,
      preMoveSentAt: null,
      playerColor: result.color,
      lastMove: result.state.moveHistory[result.state.moveHistory.length - 1] || null,
      gameResult: null,
      gameEndReason: null,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      /* Il residuo viene dal server, non da `Date.now()` di una partita che il
         client non ha visto cominciare. */
      primaMossaScadeAt: result.primaMossa ? Date.now() + result.primaMossa.restaMs : null,
      primaMossaEsito: result.primaMossa?.esito ?? null,
      /* Il rientro il conto lo porta calcolato dal server, che è l'unico a
         sapere quante semi-mosse ha messo l'apertura. */
      openingPlyCount: result.openingPlyCount ?? 0,
      /* La posizione di partenza composta, se la partita ne ha una. Senza
         spread condizionale, di proposito: vedi il campo. */
      startBoard: result.startBoard ?? null,
      /* L'apertura, che il rientro ora porta: intestazione «APERTURA N° …» e grigi
         in Mosse. Stesso oggetto se e' la stessa, cosi' il cartellino non si riaccende. */
      drawnOpening: result.roomId === get().roomId
        ? aperturaStabile(get().drawnOpening, annuncioApertura(result.opening))
        : annuncioApertura(result.opening),
      /* Un payload senza nome NON cancella l'avversario che si ha già: identico
         al ramo della partita finita mentre eri via, qui sotto, che conservava
         da sempre — e alla nota in `tournament-store.rientraInPartita`, dove il
         difetto si è visto («Anonimo» col punteggio a trattino per tutto il
         resto della partita). Solo a parità di stanza. */
      opponent: result.opponent?.oddsUserName ? {
        username: result.opponent.oddsUserName,
        rating: result.opponent.oddsRating,
      } : (result.roomId === get().roomId ? get().opponent : null),
      ...(result.variant ? { variant: toGameVariant(result.variant || 'international') } : {}),
      ...(result.timeControl ? { timeControl: result.timeControl as TimeControlPreset } : {}),
      /*
       * Le negoziazioni pendenti tornano col rientro, come il boost.
       *
       * Prima il payload non le portava e nessun evento le riallineava: chi
       * aveva offerto patta non vedeva più «proposta inviata» e chi doveva
       * risponderle non vedeva più la richiesta, mentre il SERVER l'offerta la
       * teneva — quindi un `game:accept-draw` che il client credeva impossibile
       * era ancora valido. Il colore identifica l'offerente (le offerte vivono
       * sul posto, non sul socket, che al rientro cambia).
       */
      drawOffered: nostraOfferta(result.drawOfferBy, result.color),
      opponentOfferedDraw: offertaAvversaria(result.drawOfferBy, result.color),
      /*
       * E il freno delle proposte di patta, che il rientro porta per lo stesso
       * motivo: senza, un ricarico rimetteva il bottone acceso su una quota
       * esaurita — ed e' proprio il ricarico il varco che il vecchio limite per
       * connessione lasciava aperto, perche' era chiavato sul socket.
       *
       * Un payload che non lo porta non spegne niente: si tiene quel che si ha.
       */
      ...(result.offertePatta
        ? { drawOffersLeft: result.offertePatta.restanti, drawMustMove: result.offertePatta.deviMuovere }
        : {}),
      takebackOffered: nostraOfferta(result.takebackOffer?.color ?? null, result.color),
      opponentRequestedTakeback: offertaAvversaria(result.takebackOffer?.color ?? null, result.color),
      pendingTakebackToPly: result.takebackOffer?.toPly ?? null,
      pendingTakebackPlies:
        result.takebackOffer != null
          ? Math.max(0, result.state.moveHistory.length - result.takebackOffer.toPly)
          : null,
    });
    // Tournament games land here too (only arena games are delegated above), so
    // this covers the tournament reconnect as well.
    useChatStore.getState().setChatContext({
      roomId: result.roomId,
      playerColor: result.color,
      canChat: true,
      source: 'online',
    });
    // Dopo il contesto, mai prima: un cambio di room azzera il transcript.
    if (result.chat) useChatStore.getState().restoreMessages(result.chat);
    if (result.clock) get().syncClock(result.clock);
    return;
  }

  // Game ended while we were away — render game-over instead of a frozen board.
  if (result.endedGame && result.roomId && result.state && result.color) {
    log.info('Multiplayer', 'reconnect: game ended while away', {
      roomId: result.roomId, result: result.endedGame.result, reason: result.endedGame.reason,
      tournamentId: result.tournamentId,
    });
    /*
     * Una partita di TORNEO si mostra nel torneo. Qui sotto diventava una
     * partita online qualunque: niente punti, niente torneo, nessuna strada per
     * tornarci — chi l'aveva persa per disconnessione o per assenza non lo
     * veniva a sapere nel suo contesto. Fra andata e ritorno (`returnLeg`) resta
     * la strada di sempre, che conosce il conto alla rovescia del ritorno.
     */
    if (result.tournamentId && !result.returnLeg) {
      useTournamentStore.getState().mostraPartitaFinitaMentreEriVia({
        tournamentId: result.tournamentId,
        roomId: result.roomId,
        state: result.state,
        color: result.color,
        opponent: result.opponent?.oddsUserName
          ? {
              id: result.opponent.oddsUserId ?? '',
              username: result.opponent.oddsUserName,
              rating: result.opponent.oddsRating ?? null,
              avatar: result.opponent.oddsAvatar ?? null,
            }
          : null,
        chat: result.chat,
        timeControl: result.timeControl,
        result: result.endedGame.result,
        reason: result.endedGame.reason,
        opening: result.opening,
      });
      return;
    }
    set({
      status: 'ended',
      roomId: result.roomId,
      boardState: result.state,
      preMoveBoardState: null,
      preMoveSentAt: null,
      playerColor: result.color,
      lastMove: result.state.moveHistory[result.state.moveHistory.length - 1] || null,
      gameResult: result.endedGame.result,
      gameEndReason: result.endedGame.reason,
      clockState: null,
      sessionScore: result.endedGame.sessionScore || { white: 0, black: 0 },
      canRematch: result.endedGame.canRematch || false,
      ...REMATCH_OFFER_RESET,
      // Reconnected between the legs of a match: keep the countdown running so
      // the return leg is still announced (and still auto-starts).
      ...(result.endedGame.match
        ? {
            matchLegIndex: result.endedGame.match.legIndex,
            matchTotalLegs: result.endedGame.match.totalLegs,
            matchComplete: result.endedGame.match.complete,
            matchScore: toPlayerMatchScore(result.endedGame.match.score),
          }
        : MATCH_RESET),
      ...(result.returnLeg
        ? {
            returnLegStartsAt: result.returnLeg.startsAt,
            returnLegReady: false,
            matchReadyUserIds: [],
            matchScore: toPlayerMatchScore(result.returnLeg.score),
            matchLegIndex: result.returnLeg.legIndex,
            matchTotalLegs: result.returnLeg.totalLegs,
            matchComplete: false,
          }
        : {}),
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      opponent: result.opponent?.oddsUserName ? {
        username: result.opponent.oddsUserName,
        rating: result.opponent.oddsRating,
      } : get().opponent,
      ...(result.variant ? { variant: toGameVariant(result.variant || 'international') } : {}),
      ...(result.timeControl ? { timeControl: result.timeControl as TimeControlPreset } : {}),
      /* L'apertura anche a partita finita: la scheda Mosse della fine partita la
         mostra, e senza perdeva intestazione e grigi. Il resoconto di una partita
         finita non porta `openingPlyCount`: il conto e' quello delle sue mosse. */
      drawnOpening: result.roomId === get().roomId
        ? aperturaStabile(get().drawnOpening, annuncioApertura(result.opening))
        : annuncioApertura(result.opening),
      openingPlyCount: result.openingPlyCount ?? result.opening?.moves?.length ?? 0,
      /* Anche a partita finita: la scheda Mosse della fine partita si naviga. */
      startBoard: result.startBoard ?? null,
    });
    // The game is over but the room is still alive: chat (and the abuse report
    // that needs its roomId) stays available, as it does after a normal game end.
    useChatStore.getState().setChatContext({
      roomId: result.roomId,
      playerColor: result.color,
      canChat: true,
      source: 'online',
    });
    if (result.chat) useChatStore.getState().restoreMessages(result.chat);
    return;
  }

  // Server reports no active or recently-ended game, yet we're still holding a
  // live in-progress board (roomId + boardState, not terminal): the game vanished
  // while we were away (room cleaned up after a long absence). Clear the stale
  // board so the player isn't stranded. Guarded on boardState so it never touches
  // the matchmaking-found window (roomId set, board still null).
  //
  // Vale anche per la risposta `unauthenticated`: quando arriva fin qui,
  // `checkActiveGame` ha gia' tentato e fallito il recupero dell'identita' (col
  // rinnovo ritentato, e col tetto che gli impedisce di restare appeso), quindi
  // non c'e' piu' niente da aspettare. Per `timedOut` invece vale solo AL
  // SECONDO GIRO: era equiparato al «no» autorevole dal primo, e cosi' un ack
  // perso buttava via una partita viva (vedi `ilServerNonHaGuardato` e il ramo
  // qui sotto). Al secondo la ragione di prima torna valida: due ack persi di
  // fila vogliono dire server irraggiungibile, e non c'e' un terzo colpo da
  // aspettare.
  //
  // Tenersi la partita lascerebbe una damiera viva su cui non si puo' ne'
  // muovere ne' abbandonare: `onConnect` ha gia' messo status:'connected', la
  // board non e' terminale, quindi niente fine partita, niente RematchPanel e
  // nessun `onLeave` per GameScreen.
  //
  // Rilasciarla porta FUORI, alla home: se ne occupa l'effetto di `App.tsx` che
  // consulta `laPartitaOnlineEStataRilasciata`. Non e' un dettaglio di
  // presentazione — e' la meta' che mancava: senza quella uscita, rilasciare
  // lasciava il giocatore sulla stessa damiera vuota da cui lo si voleva
  // togliere, e la scelta di rilasciare non avrebbe avuto senso.
  //
  // E la stessa cosa vale per una partita d'ARENA, che pero' qui non si vede:
  // la sua posizione sta nell'arena-store, quindi questo `boardState` e' nullo
  // e nessuno dei controlli qui sotto scatta. Senza la riga qui sopra la
  // damiera d'arena restava appesa esattamente come quella online.
  //
  // ⚠️ MA un ack PERSO non e' un «no»: vedi `ilServerNonHaGuardato`. Sul primo
  // non si rilascia niente — si tiene la partita e si richiede una volta —
  // perche' la partita puo' essere ancora viva e perche' per la damiera di
  // torneo il rilascio e' senza ritorno.
  if (!result.hasActiveGame) {
    const inPartitaDaPerdere = get().boardState !== null
      || useTournamentStore.getState().status === 'playing'
      || useArenaStore.getState().status === 'playing';
    if (inPartitaDaPerdere && ilServerNonHaGuardato(result) && !sondaGiaRipetuta) {
      log.warn('Multiplayer', 'rientro: il server non ha guardato, non rilascio e richiedo', {
        fraMs: ATTESA_RISONDA_MS,
      });
      risondaPiuTardi(set, get);
      return;
    }
    if (ilServerNonHaGuardato(result)) {
      /* Si rilascia comunque, e si dice perche': la ripetizione e' gia' stata
         fatta (o non c'era niente da perdere). Due risposte di fila che non
         guardano vogliono dire server irraggiungibile, e tenersi una damiera su
         cui non si puo' ne' muovere ne' abbandonare e' peggio. */
      log.warn('Multiplayer', 'rientro: secondo ack perso, rilascio', {
        giaRipetuta: sondaGiaRipetuta, inPartitaDaPerdere,
      });
      /*
       * E l'EPISODIO si chiude qui: la prossima caduta ha diritto al suo
       * tentativo. Senza questa riga il fermo restava alzato fino alla prima
       * risposta autorevole, e una sola che non arrivasse disarmava la
       * protezione per tutto il resto della sessione — un blip di rete dopo e
       * si tornava a buttare via una partita viva, in silenzio.
       *
       * Non apre nessun ciclo: il timer della ripetizione e' l'unico a
       * richiedere da se', e qui non se ne arma nessuno. A chiamare di nuovo
       * sono solo un `connect`, un risveglio in primo piano o un resync.
       */
      sondaGiaRipetuta = false;
    }
    useArenaStore.getState().rilasciaPartita();
    // E vale IDENTICO per una partita di torneo, per la stessa ragione: la sua
    // posizione sta nel tournament-store, quindi qui `boardState` e' nullo e
    // nessun controllo qui sotto scatta. Senza questa riga lo stato 'playing'
    // del torneo restava appiccicato per sempre.
    useTournamentStore.getState().rilasciaPartita();
  }
  if (
    !result.hasActiveGame &&
    get().boardState !== null &&
    get().roomId !== null &&
    get().status !== 'ended' &&
    !get().gameResult
  ) {
    log.warn('Multiplayer', 'reconnect: active game gone — clearing stale board', {
      roomId: get().roomId, status: get().status,
    });
    set({
      /* Perche' si torna alla home: la partita e' finita mentre si era via e il
         server non sa piu' dirci come. Meglio dirlo che sparire e basta. */
      partitaTerminataMentreEriVia: true,
      status: isConnected() ? 'connected' : 'disconnected',
      roomId: null,
      boardState: null,
      preMoveBoardState: null,
      preMoveSentAt: null,
      playerColor: null,
      lastMove: null,
      gameResult: null,
      gameEndReason: null,
      clockState: null,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
    });
  }
}

/**
 * The pending rematch offer, cleared in every path that ends one (new game,
 * game end, rematch start, abort, leaving). One block instead of the pair of
 * booleans repeated at nine call sites, so a new field can't be forgotten at
 * one of them.
 */
const REMATCH_OFFER_RESET = {
  opponentWantsRematch: false,
  isRequestingRematch: false,
  rematchOfferExpiresAt: null,
  rematchNotice: null,
} satisfies Partial<MultiplayerStore>;

/**
 * Quota piena e nessun blocco: ogni partita NUOVA riparte da qui.
 *
 * E' l'anello che si dimentica. Se il server azzera a ogni partita (lo fa, in
 * `resetForNewGame`) e il client no, la rivincita riparte col bottone spento
 * sulla quota della partita prima — ed e' lo stesso buco in cui l'offerta di
 * patta era gia' caduta una volta, sopravvivendo alla rivincita come «una patta
 * pronta da incassare».
 */
const PATTA_BUDGET_RESET = {
  drawOffersLeft: MAX_OFFERTE_PATTA_PER_PARTITA,
  drawMustMove: false,
} satisfies Partial<MultiplayerStore>;

// Cleared wherever the draw flags are cleared (new game, game end, rematch, abort).
const TAKEBACK_RESET = {
  takebackOffered: false,
  opponentRequestedTakeback: false,
  pendingTakebackPlies: null,
  pendingTakebackToPly: null,
  takebackDeclinedNotice: false,
  takebackAcceptedNotice: false,
} satisfies Partial<MultiplayerStore>;

/** Clears every trace of a two-leg match (leaving the game, brand-new game). */
const MATCH_RESET = {
  matchLegIndex: null,
  matchTotalLegs: null,
  matchScore: null,
  matchComplete: false,
  returnLegStartsAt: null,
  returnLegReady: false,
  matchReadyUserIds: [],
} satisfies Partial<MultiplayerStore>;

/**
 * L'avviso di disconnessione (o di rientro) riguarda un'altra stanza?
 *
 * `game:player-disconnected` arriva a tutta la stanza, e chi passa a un'altra
 * partita mentre l'avversario della prima e' in grazia — una sfida accettata con
 * la partita vecchia ancora aperta — se lo vedeva applicare alla nuova: sul nome
 * del nuovo avversario restava «Disconnesso», i secondi arrivavano a zero e la
 * scritta restava li' per tutta la partita (29 settembre). Senza `roomId` (server
 * vecchio) o senza una stanza corrente non si scarta niente: e' il comportamento
 * di prima.
 */
function avvisoDiUnAltraStanza(roomIdAvviso: string | undefined, roomIdCorrente: string | null | undefined): boolean {
  return !!roomIdAvviso && !!roomIdCorrente && roomIdAvviso !== roomIdCorrente;
}

/**
 * Turn the server's userId-keyed match score into "you vs opponent". The server
 * keys by user precisely because the colours swap between legs.
 */
function toPlayerMatchScore(score: Record<string, number> | undefined): { you: number; opponent: number } | null {
  if (!score) return null;
  const myId = useAuthStore.getState().user?.id;
  let you = 0;
  let opponent = 0;
  for (const [userId, points] of Object.entries(score)) {
    if (myId && userId === myId) you += points;
    else opponent += points;
  }
  return { you, opponent };
}

/**
 * i18n key for a lobby rejection.
 *
 * The server's message is English prose meant for logs; the code is the part
 * worth showing a player, because each refusal needs different wording ("you
 * are already in a game" vs "that table is gone" vs "leave the arena first").
 * Anything unmapped falls back to a generic line rather than leaking English.
 */
const LOBBY_ERROR_KEYS: Record<string, string> = {
  AUTH_REQUIRED: 'online.errors.authRequired',
  ALREADY_IN_GAME: 'online.errors.alreadyInGame',
  IN_COMPETITION: 'lobby.errors.inCompetition',
  RATE_LIMITED: 'lobby.errors.rateLimited',
  TABLE_NOT_FOUND: 'lobby.errors.tableGone',
  TABLE_TAKEN: 'lobby.errors.tableGone',
  // Non «il tavolo non c'e' piu'»: il tavolo c'era, e' chi l'aveva aperto a non
  // esserci. La differenza conta per chi legge, perche' dice che non ha
  // sbagliato niente e puo' semplicemente sedersi altrove.
  HOST_GONE: 'lobby.errors.hostGone',
  OWN_TABLE: 'lobby.errors.ownTable',
  // «Solo iscritti»: non un guasto, una condizione d'ingresso. Mancava, e
  // cadeva sul messaggio generico — ora che un ospite puo' incontrarla dal
  // riquadro «siediti o sfida» dei collegati, deve dire cosa manca.
  REGISTERED_ONLY: 'online.challenge.registeredOnly',
  INVALID_TIME_CONTROL: 'lobby.errors.invalidSettings',
  INVALID_VARIANT: 'lobby.errors.invalidSettings',
  INVALID_SETTINGS: 'lobby.errors.invalidSettings',
  START_FAILED: 'lobby.errors.startFailed',
};

function lobbyErrorKey(err: unknown): string {
  if (err instanceof LobbyRequestError) {
    return LOBBY_ERROR_KEYS[err.code] ?? 'lobby.errors.generic';
  }
  return 'online.connectionFailed';
}

export const useMultiplayerStore = create<MultiplayerStore>((set, get) => ({
  // Initial state
  status: 'disconnected',
  presenzaScaduta: false,
  roomId: null,
  playerColor: null,
  variant: GameVariant.INTERNATIONAL,
  error: null,
  partitaTerminataMentreEriVia: false,
  boardState: null,
  preMoveBoardState: null,
  preMoveSentAt: null,
  lastMove: null,
  gameResult: null,
  gameEndReason: null,
  gameStartedAtMs: null,
  primaMossaScadeAt: null,
  primaMossaEsito: null,
  openingPlyCount: 0,
  startBoard: null,
  clockState: null,
  timeControl: null,
  isRated: true,
  searchTimeControl: null,
  searchStartTime: null,
  queuePosition: null,
  estimatedWait: null,
  opponent: null,
  abortNotice: null,
  drawnOpening: null,
  sessionScore: { white: 0, black: 0 },
  ...REMATCH_OFFER_RESET,
  canRematch: false,
  matchLegIndex: null,
  matchTotalLegs: null,
  matchScore: null,
  matchComplete: false,
  returnLegStartsAt: null,
  returnLegReady: false,
  matchReadyUserIds: [],

  // Draw offer state
  drawOffered: false,
  opponentOfferedDraw: false,
  drawDeclinedNotice: false,
  ...PATTA_BUDGET_RESET,

  // Takeback state
  takebackOffered: false,
  opponentRequestedTakeback: false,
  pendingTakebackPlies: null,
  pendingTakebackToPly: null,
  takebackDeclinedNotice: false,
  takebackAcceptedNotice: false,

  // Disconnection state
  isOpponentDisconnected: false,
  opponentDisconnectedColor: null,
  graceTimeRemaining: null,
  opponentMaiArrivato: false,
  opponentDecideLOrologio: false,

  // Connect to server
  connect: async () => {
    log.info('Multiplayer', 'connect() called');
    // If the server evicted us because another tab/device took over this account
    // (force-disconnect 'new-session'), do NOT reconnect — reconnecting would evict
    // that newer session and start an eviction ping-pong. An explicit disconnect
    // (logout / identity change) clears this latch.
    if (wasEvictedByNewSession()) {
      log.warn('Multiplayer', 'connect() skipped: session taken over by another tab/device');
      return;
    }
    // Ensure we have a fresh-enough auth token before connecting. Only refresh when
    // the current token is near expiry — refreshing on every connect amplified
    // reconnect churn into a POST /refresh storm that tripped the rate limiter.
    const authState = useAuthStore.getState();
    const originalToken = authState.accessToken;
    /*
     * `originalToken || isAuthenticated` e non il solo token.
     *
     * `accessToken` non e' persistito: al boot un utente registrato ha
     * `isAuthenticated: true` e nessun token in memoria. Con la guardia sul
     * solo token si saltava tutto -- niente rinnovo, niente
     * `setSocketAuthToken` -- e il socket nasceva anonimo. Il rinnovo, dal
     * canto suo, si autoescludeva perche' «non c'e' nessun token da
     * rinnovare»: l'anonimato diventava permanente.
     */
    if (originalToken || authState.isAuthenticated) {
      if (!originalToken || accessTokenExpiresSoon(originalToken)) {
        try {
          await authState.refreshToken();
        } catch (err) {
          // Refresh failed — proceed with existing token (may be stale → auth socket race)
          log.warn('Multiplayer', 'pre-connect token refresh failed; using existing token', { error: err instanceof Error ? err.message : String(err) });
        }
      }
      // Use refreshed token, or fall back to original if refresh cleared it
      const tokenToUse = useAuthStore.getState().accessToken || originalToken;
      setSocketAuthToken(tokenToUse);
    }

    const socketCallbacks: Parameters<typeof initSocket>[0] = {
      onConnect: async () => {
        log.info('Multiplayer', 'socket connected');
        /*
         * La presenza torna valida PRIMA che i dati siano tornati, ed e'
         * giusto cosi': `avvisaConnessione()` parte nello stesso giro sincrono
         * di questo callback (`handleConnected` in services/socket.ts), e chi
         * ascolta accende subito il proprio caricamento. React vede nello
         * stesso lotto la bandiera abbassata e lo spinner acceso, quindi si
         * passa dall'avviso allo spinner e non al vuoto.
         */
        fermaTimerScadenzaPresenza();
        set({ status: 'connected', presenzaScaduta: false });

        // Ogni riconnessione riallinea anche la LOBBY, non solo la partita: il
        // proprio tavolo puo' essere stato ritirato mentre si era via, e
        // l'annuncio se n'e' andato a un socket che non c'era piu'.
        risincronizzaLobby();

        // Check if user has an active game to rejoin (or a game that ended while away)
        try {
          const result = await checkActiveGame();
          applyReconnectResult(result, set, get);
        } catch (error) {
          log.error('Multiplayer', 'Error checking active game', error);
        }
      },
      /*
       * L'altro rientro: quello che NON passa da una riconnessione.
       *
       * Al ritorno in primo piano `risvegliaSocket` sonda il server, e se il
       * socket e' vivo non c'e' nessun `connect` — quindi nessun `onConnect` e
       * nessuno che applichi la risposta. Finche' quella risposta veniva
       * buttata via, chi rientrava su una partita finita mentre era in secondo
       * piano restava su una damiera viva dove nessun gesto faceva niente.
       */
      onRientro: (result) => {
        log.info('Multiplayer', 'risveglio: applico il resoconto del rientro', {
          hasActiveGame: result.hasActiveGame,
          endedGame: !!result.endedGame,
        });
        // Il socket e' vivo: se il timer di scadenza era armato (o era gia'
        // scattato mentre l'app stava in secondo piano) non ha piu' ragione.
        fermaTimerScadenzaPresenza();
        if (get().presenzaScaduta) set({ presenzaScaduta: false });
        // Il socket e' rimasto vivo, ma la scheda era in secondo piano: gli
        // eventi della lobby arrivati nel frattempo sono stati consegnati a una
        // pagina che non li stava guardando. Stessa domanda di `onConnect`.
        risincronizzaLobby();
        applyReconnectResult(result, set, get);
      },
      onDisconnect: () => {
        log.warn('Multiplayer', 'socket disconnected');
        set({ status: 'disconnected' });
        /*
         * Le liste NON si svuotano subito: un singhiozzo di rete dura meno
         * dell'attesa, e svuotarle a ogni riconnessione da un secondo sarebbe
         * rumore. Scaduta l'attesa, invece, non c'e' piu' niente da difendere:
         * i tavoli che si vedono sono morti (l'host perde il suo all'istante,
         * lato server) e i collegati sono quelli di prima.
         */
        fermaTimerScadenzaPresenza();
        timerScadenzaPresenza = setTimeout(() => {
          timerScadenzaPresenza = null;
          get().invalidaPresenza();
        }, ATTESA_INVALIDAZIONE_PRESENZA_MS);
      },
      onGameStart: ({ state, clock, color, roomId, opponent, variant, timeControl, opening, rated, match, source, primaMossa, startBoard }) => {
        log.info('Multiplayer', 'onGameStart received', {
          color,
          roomId,
          opponent: opponent ? { username: opponent.username, rating: opponent.rating } : null,
          variant: variant || 'not-provided',
          timeControl: timeControl || 'not-provided',
          rated,
          stateVariant: state.variant,
        });
        const tournamentStatus = useTournamentStore.getState().status;
        const arenaStatus = useArenaStore.getState().status;
        const mpStatus = get().status;
        if (tournamentStatus === 'match-found' || tournamentStatus === 'playing') {
          log.info('Multiplayer', 'onGameStart → delegated to tournament');
          useTournamentStore.getState().setGameStart(state, clock);
          return;
        }
        if (arenaStatus === 'matchFound' || arenaStatus === 'playing' || arenaStatus === 'gameEnd') {
          log.info('Multiplayer', 'onGameStart → delegated to arena');
          /*
           * L'apertura si inoltra INTERA, non solo la sua lunghezza.
           * All'arena serve per due cose distinte, e le ricava lei
           * (`arena-store.setGameStart`): quante semi-mosse ha messo il
           * server — `opening.moves` ha una voce per semi-mossa, `parseMoves`
           * mappa 1:1 in `shared/types/opening-lottery.ts` — per contare la
           * finestra del boost al netto dell'apertura, e il testo da mostrare
           * nel cartellino sopra la scacchiera. Fermarsi al numero lascia il
           * giocatore davanti a tre semi-mosse già giocate senza dirgli da
           * dove vengono; non passare niente rimette in piedi il difetto di
           * ieri, con la finestra del boost chiusa prima di aprirsi.
           */
          useArenaStore.getState().setGameStart(state, clock, opening, primaMossa);
          return;
        }
        // Una sfida (diretta o da link) è una partita che il server ha davvero
        // aperto per noi: registrata in activeGamesMap, con l'orologio che gira.
        // Scartarla la lascerebbe viva sul solo server, su una scacchiera che
        // non vediamo, fino alla perdita per tempo — e il pairing d'arena si
        // protegge già da solo rifiutando chi ha una partita aperta. Le guardie
        // qui sotto servono contro gli eventi stantii della PROPRIA competizione,
        // non contro questo.
        const isChallengeGame = source === 'challenge';
        // Drop stale game events from resigned pending matches when user is in arena lobby
        // (but NOT if multiplayer is actively searching — that's a legit matchmaking game:start)
        if (!isChallengeGame && mpStatus !== 'searching' && useArenaStore.getState().isJoined && useArenaStore.getState().currentArena) {
          log.info('Multiplayer', 'onGameStart → dropped (arena lobby)');
          return;
        }
        // Drop stale game events from tournament games when user is in an active tournament
        if (!isChallengeGame && mpStatus !== 'searching' && useTournamentStore.getState().isJoined && useTournamentStore.getState().currentTournament && tournamentStatus !== 'finished') {
          log.info('Multiplayer', 'onGameStart → dropped (tournament)');
          return;
        }
        // Sta per comparire una scacchiera, quindi il flusso della sfida-link è
        // finito. L'overlay del challenger è globale — si disegna anche sopra la
        // gamescreen — e la prenotazione dell'amico è appena stata consumata.
        // Qui convergono entrambi i modi in cui arriva una partita da link:
        // l'amico che accetta mentre guardiamo, e la partenza differita che il
        // server rigioca al rientro da WhatsApp. Agganciarsi a
        // `challenge:link-claimed` mancherebbe il secondo caso: quell'evento
        // viene emesso verso un socket che in quel momento non era connesso.
        useOnlineStore.getState().clearLinkChallenges();
        /*
         * E spegne anche l'attesa in lobby.
         *
         * Finora `myTableId` lo azzerava SOLO `lobby:table-removed`: un unico
         * pacchetto, e se si perdeva (riconnessione a cavallo della partenza,
         * ack del proprio tavolo arrivato dopo il suo stesso ritiro) il banner
         * «Sei in coda» restava montato SOPRA la scacchiera per tutta la
         * partita — con, peggio, la guardia app-in-background del banner armata,
         * pronta a chiamare `cancelMyTable()` a partita in corso.
         *
         * Una partita che comincia E' la fine dell'attesa: non ha bisogno che
         * qualcuno gliela venga a raccontare.
         */
        useLobbyStore.getState().setMyTable(null);
        const resolvedVariant = variant ? toGameVariant(variant) : get().variant;
        const resolvedTimeControl = timeControl ? timeControl as TimeControlKey : get().timeControl;
        // A genuinely new game (first match, or a rematch in a fresh room) vs a
        // duplicate/echoed game:start for the room we're already in. Only a new
        // game stamps the start time and fires the canonical `game-started`, so
        // rematches count once and echoes never double-count.
        const isNewGame = !!roomId && roomId !== get().roomId;
        log.info('Multiplayer', 'onGameStart → setting state', {
          resolvedVariant,
          resolvedTimeControl,
          opponentUsername: opponent?.username,
          opponentRating: opponent?.rating,
          playerColor: color,
        });
        set({
          status: 'playing',
          // Residui dell'attesa: `onLobbyTableRemoved` li azzera solo se lo
          // stato e' ancora 'searching', e a quel punto questo `set` l'ha gia'
          // portato a 'playing'. Restavano quindi appesi per tutta la partita.
          searchTimeControl: null,
          searchStartTime: null,
          boardState: state,
          preMoveBoardState: null,
          preMoveSentAt: null,
          lastMove: null,
          abortNotice: null,
          // Surface a drawn opening for the transient in-game banner (null for free).
          drawnOpening: annuncioApertura(opening),
          ...(color ? { playerColor: color } : {}),
          ...(roomId ? { roomId } : {}),
          ...(opponent ? { opponent } : {}),
          ...(variant ? { variant: resolvedVariant } : {}),
          ...(timeControl ? { timeControl: resolvedTimeControl } : {}),
          // Set unconditionally (server is source of truth): only an explicit
          // `false` marks the game casual; a missing flag (older server) defaults
          // to rated so behavior falls back to the guest/bot heuristic downstream.
          isRated: rated !== false,
          ...(isNewGame ? { gameStartedAtMs: Date.now() } : {}),
          /*
           * Una partita NUOVA parte con l'avversario presente. Lo stato di
           * disconnessione lo spegnevano fine partita, rivincita, ritorno e
           * rientro, ma non l'inizio: chi accettava una sfida mentre l'avversario
           * della partita vecchia era in grazia si portava dietro «Disconnesso»
           * sul nome di quello nuovo. Solo per una stanza nuova: un `game:start`
           * ripetuto per la stessa partita non deve spegnere una caduta vera.
           */
          ...(isNewGame
            ? { isOpponentDisconnected: false, opponentDisconnectedColor: null, graceTimeRemaining: null }
            : {}),
          /* E la quota di proposte di patta, per la stessa ragione della riga
             qui sopra: una partita nuova riparte con tutte le sue. Solo per una
             stanza nuova — un `game:start` ripetuto non deve regalare quota in
             mezzo a una partita. */
          ...(isNewGame ? PATTA_BUDGET_RESET : {}),
          /*
           * La scadenza della prima mossa si arma ADESSO, non quando il server
           * l'ha decisa: il campo porta una DURATA, così il conto non dipende
           * dall'orologio del dispositivo. Una partita che non ne ha (torneo)
           * azzera, invece di trascinarsi dietro quella di prima.
           */
          primaMossaScadeAt: primaMossa ? Date.now() + primaMossa.restaMs : null,
          primaMossaEsito: primaMossa?.esito ?? null,
          openingPlyCount: opening?.moves?.length ?? 0,
          /* La posizione composta di questa partita, `null` per tutte le altre.
             Non appiccicosa: vedi il campo. La delega ad arena e torneo qui
             sopra ritorna PRIMA di questo `set` e lascia il valore com'era —
             innocuo, perche' quelle due schermate entrano con
             `enterOnlineGame()` senza argomenti, e `reset()` lo azzera. */
          startBoard: startBoard ?? null,
          // Two-leg match ("andata e ritorno"): only drawn-opening challenges
          // carry this; every other game clears it.
          ...(match
            ? {
                matchLegIndex: match.legIndex,
                matchTotalLegs: match.totalLegs,
                matchComplete: match.complete,
                matchScore: toPlayerMatchScore(match.score),
                returnLegStartsAt: null,
                returnLegReady: false,
                matchReadyUserIds: [],
              }
            : MATCH_RESET),
        });
        // Hand the conversation to chat-store. Same roomId as before (a rematch
        // reuses the server room) keeps the transcript; a new room starts clean.
        useChatStore.getState().setChatContext({
          roomId: roomId ?? get().roomId,
          playerColor: color ?? get().playerColor,
          canChat: true,
          source: 'online',
        });
        if (clock) {
          get().initClock(clock);
        }
        if (isNewGame) {
          // A genuinely new game — return the local board to live. syncFromExternal
          // preserves review/selection across re-syncs, so review state left over from
          // a previous game (e.g. scrolled back on its game-over screen) would otherwise
          // make this fresh game render the old position. Guarded by isNewGame so a
          // duplicate/echoed game:start never yanks a mid-game review.
          const gs = useGameStore.getState();
          gs.reviewToIndex(null);
          gs.clearPremove();
          // Canonical online `game-started` — the single convergence point for
          // matchmaking, challenge-accept, and rematch. Arena/tournament games
          // are delegated away above and covered by their own events.
          // Stesso punto, stessa domanda: `isNewGame` e' gia' la guardia che
          // distingue una partita vera da un `game:start` rigiocato, quindi qui
          // il suono non ha bisogno di nessuna chiave in piu'.
          playSound('match-found');
          trackGameStarted({ variant: resolvedVariant, timeControl: resolvedTimeControl ?? 'none', mode: 'multiplayer', ruleMode: state?.ruleMode });
          trackFirstGameEver('multiplayer');
          trackVariantBreadth(resolvedVariant);
        }
      },
      onGameUpdate: (state, lastMove, clock, roomId) => {
        log.info('Multiplayer', 'onGameUpdate entry', { roomId, from: lastMove?.from, to: lastMove?.to, currentTurn: state.currentTurn });
        const destinatario = destinatarioPerStanza(roomId, get().roomId);
        const tournamentStatus = useTournamentStore.getState().status;
        const arenaStatus = useArenaStore.getState().status;
        if (
          destinatario === 'torneo' ||
          (destinatario === null && (tournamentStatus === 'playing' || tournamentStatus === 'match-found'))
        ) {
          log.info('Multiplayer', 'onGameUpdate → delegated to tournament');
          useTournamentStore.getState().setGameUpdate(state, lastMove, clock, roomId);
          return;
        }
        if (
          destinatario === 'arena' ||
          (destinatario === null && (arenaStatus === 'playing' || arenaStatus === 'matchFound' || arenaStatus === 'gameEnd'))
        ) {
          log.info('Multiplayer', 'onGameUpdate → delegated to arena');
          useArenaStore.getState().setGameUpdate(state, lastMove, clock, roomId);
          return;
        }
        if (destinatario === 'nessuno') {
          /* Una stanza che non e' di nessuna delle tre damiere: l'eco di una
             partita gia' lasciata. Si scarta SAPENDO perche', che e' la
             differenza con lo scarto di prima. */
          log.warn('Multiplayer', 'onGameUpdate scartato: stanza di nessuno', { roomId });
          breadcrumbDesync('update-stanza-estranea', { roomId, mio: get().roomId });
          return;
        }
        if (destinatario === null) {
          /*
           * Ripiego per la finestra di deploy, con l'eccezione che gli mancava.
           *
           * `onGameStart` ce l'ha e la motiva (`isChallengeGame`): una sfida e'
           * una partita che il server ha davvero aperto per noi, e buttarla la
           * lascerebbe viva sul solo server fino alla perdita per tempo. Qui
           * quella riga non c'era, quindi la partita si apriva e poi restava
           * cieca. Se il multiplayer e' lui stesso in partita, l'aggiornamento
           * e' suo: le due guardie qui sotto servono contro gli eventi stantii
           * di una competizione, non contro la damiera che si ha davanti.
           */
          const inPartitaNostra = get().status === 'playing' && get().roomId !== null;
          if (!inPartitaNostra && useArenaStore.getState().isJoined && useArenaStore.getState().currentArena) {
            log.warn('Multiplayer', 'onGameUpdate dropped (stale arena)');
            breadcrumbDesync('update-scartato-arena-stantia');
            return; // Drop stale arena game update
          }
          if (!inPartitaNostra && useTournamentStore.getState().isJoined && useTournamentStore.getState().currentTournament) {
            log.warn('Multiplayer', 'onGameUpdate dropped (stale tournament)');
            breadcrumbDesync('update-scartato-torneo-stantio');
            return; // Drop stale tournament game update
          }
        }
        // Server confirmed a move: this is the authoritative state, so drop any
        // optimistic rollback snapshot. A non-null snapshot means this update is
        // the echo of our own optimistic move — makeMove already played the sound
        // instantly, so skip it here to avoid a delayed double sound.
        const wasOptimistic = get().preMoveBoardState !== null;
        log.info('Multiplayer', 'onGameUpdate reconcile', { wasOptimistic });
        // A forward move implicitly declines any pending takeback. The server also
        // emits game:takeback-declined (which arrives after this game:update and can
        // still set the "declined" notice); this clears the offer flags against any
        // event-ordering race so no stale banner survives a move.
        const hadTakeback = get().takebackOffered || get().opponentRequestedTakeback;
        set({
          boardState: state,
          lastMove,
          preMoveBoardState: null,
          preMoveSentAt: null,
          ...(hadTakeback ? TAKEBACK_RESET : {}),
          /* Ho mosso io? Allora posso riproporre patta. Vedi
             `utils/sblocco-patta.ts`: il server non ha modo di annunciarlo
             (questo e' uno stato del POSTO, e `game:update` va a tutta la
             stanza), quindi si deriva dal tratto. */
          ...sbloccoPattaDopoMossa(state.currentTurn, get().playerColor, get().drawMustMove),
        });
        if (clock) {
          get().syncClock(clock);
        }
        if (lastMove && !wasOptimistic) {
          log.info('Multiplayer', 'onGameUpdate → move sound', { from: lastMove.from, to: lastMove.to });
          // isPromotion is stripped from lastMove by the server schema; read it from
          // the authoritative broadcast board state (server pushes the resolved move).
          // `isCapture`: una presa suona SEMPRE `move`, mai `promote`. Quando e' una presa
          // multipla questo e' il SALTO 1 — il replay del fantasma (`syncFromExternal`)
          // suona i successivi e la promozione sull'ultimo. Le due chiamate cadono a un
          // fotogramma di distanza sullo stesso elemento audio in cache, quindi si sente
          // un suono solo: e' proprio la regola «presa → move» a renderlo innocuo.
          const ultima = state.moveHistory.at(-1);
          playMoveSound(ultima?.isPromotion ?? false, 'auto', (ultima?.captures.length ?? 0) > 0);
          vibrate(40);
        }
      },
      /*
       * LA GIORNATA, appena il server la conferma.
       *
       * Arriva al socket di questo giocatore e non alla stanza, quindi qui non
       * serve nessuno smistamento per `roomId`: se è arrivato, riguarda lui.
       *
       * `partiteOggi === target`, uguaglianza esatta e non `>=`: fa scattare la
       * festa UNA volta sola nella giornata senza ricordarsi niente. Alzare il
       * bersaglio a metà giornata la rimanda al numero nuovo; abbassarlo sotto
       * le partite già giocate non la fa più scattare — e va bene, la card dice
       * già «Obiettivo raggiunto».
       *
       * Il bersaglio si legge dalle PREFERENZE e non dal payload: il server non
       * lo possiede (`DailyGoalResult` porta solo `played`, e un `target` lì
       * dentro era «un numero finto che sembrava la fonte della verità», tolto
       * di proposito).
       */
      onDayProgress: (data) => {
        const target = usePreferencesStore.getState().dailyGoalTarget;
        mostraFestaGiornata({
          giorni: data.streak?.current,
          obiettivo: data.partiteOggi === target ? target : undefined,
        });
      },
      onGameEnd: (result, reason, ratings, sessionScore, canRematch, match, roomId) => {
        log.info('Multiplayer', 'onGameEnd entry', { roomId, result, reason });
        // Before the tournament/arena routing below returns: those games are
        // written to `games` too, so they count toward the dashboard's activity
        // streak, daily goal and heatmap just like a plain online game.
        markDashboardStale();
        /*
         * Prima la STANZA, come `onGameUpdate`. Senza, a chi era iscritto a un
         * torneo (anche finito: `isJoined` si spegne solo al logout) o a
         * un'arena la fine delle PROPRIE sfide veniva buttata dalle guardie
         * qui sotto: damiera viva per sempre, e con una proposta di patta in
         * sospeso una barra Accetta/Rifiuta che non rispondeva piu' (30 set).
         * Lo stato degli altri store resta il ripiego per un server che il
         * `roomId` non lo manda ancora.
         */
        const destinatario = destinatarioPerStanza(roomId, get().roomId);
        const tournamentStatus = useTournamentStore.getState().status;
        const arenaStatus = useArenaStore.getState().status;
        /*
         * `match-found` insieme a `playing`: e' la finestra fra
         * `tournament:match` e l'ingresso in partita, e da quando il torneo NON
         * registra piu' i propri `onGameEnd`/`onClockSync` (li sovrascriveva a
         * quelli del multiplayer, vedi il commento in `tournament-store`) e'
         * questo lo smistamento unico. Un abbandono immediato dell'avversario
         * arriva proprio in quella finestra.
         */
        if (
          destinatario === 'torneo' ||
          (destinatario !== 'multiplayer' && destinatario !== 'arena' &&
            (tournamentStatus === 'playing' || tournamentStatus === 'match-found'))
        ) {
          log.info('Multiplayer', 'onGameEnd → routed to tournament');
          const playerColor = useTournamentStore.getState().playerColor;
          let myRatingChange: { oldRating: number; newRating: number } | undefined;
          if (ratings && playerColor) {
            const myRating = playerColor === 'white' ? ratings.white : ratings.black;
            myRatingChange = { oldRating: myRating.oldRating, newRating: myRating.newRating };
            const tournament = useTournamentStore.getState().currentTournament;
            useAuthStore.getState().updateRating(myRating.newRating, tournament?.variant, tournament?.timeControl);
          }
          useTournamentStore.getState().setGameEnd(result, reason, myRatingChange);
          return;
        }
        if (
          destinatario === 'arena' ||
          (destinatario !== 'multiplayer' &&
            (arenaStatus === 'playing' || arenaStatus === 'matchFound' || arenaStatus === 'gameEnd'))
        ) {
          log.info('Multiplayer', 'onGameEnd → routed to arena');
          useArenaStore.getState().setGameEnd(result, reason);
          const playerColor = useArenaStore.getState().playerColor;
          if (ratings && playerColor) {
            const myRating = playerColor === 'white' ? ratings.white : ratings.black;
            const arena = useArenaStore.getState().currentArena;
            useAuthStore.getState().updateRating(myRating.newRating, arena?.variant as string | undefined, arena?.timeControl);
          }
          return;
        }
        if (destinatario === 'nessuno') {
          /* La fine di una stanza che non e' di nessuna delle tre damiere:
             una partita gia' lasciata. Non deve chiudere quella in corso. */
          log.warn('Multiplayer', 'onGameEnd scartato: stanza di nessuno', { roomId, mio: get().roomId });
          return;
        }
        if (destinatario === null) {
          // Ripiego senza `roomId`, con l'eccezione di `onGameUpdate`: la
          // damiera che si ha davanti non e' un evento stantio.
          const inPartitaNostra = get().status === 'playing' && get().roomId !== null;
          // Drop stale game events from resigned pending matches when user is in arena lobby
          if (!inPartitaNostra && useArenaStore.getState().isJoined && useArenaStore.getState().currentArena) {
            log.info('Multiplayer', 'onGameEnd dropped (arena lobby)');
            return;
          }
          // Drop stale game events from tournament games
          if (!inPartitaNostra && useTournamentStore.getState().isJoined && useTournamentStore.getState().currentTournament) {
            log.info('Multiplayer', 'onGameEnd dropped (tournament)');
            return;
          }
        }

        const playerColor = get().playerColor;
        const currentOpponent = get().opponent;
        const { variant, timeControl } = get();

        log.info('Multiplayer', 'onGameEnd processing', {
          result,
          reason,
          playerColor,
          variant,
          timeControl,
          hasRatings: !!ratings,
          opponentBefore: currentOpponent ? { username: currentOpponent.username, rating: currentOpponent.rating } : null,
        });

        // Update user's rating in auth store if we have rating data
        if (ratings && playerColor) {
          const myRating = playerColor === 'white' ? ratings.white : ratings.black;
          log.info('Multiplayer', 'onGameEnd → updateRating', {
            playerColor,
            oldRating: myRating.oldRating,
            newRating: myRating.newRating,
            variant,
            timeControl,
          });
          useAuthStore.getState().updateRating(myRating.newRating, variant, timeControl ?? undefined);
        } else {
          log.warn('Multiplayer', 'onGameEnd → no rating update', { hasRatings: !!ratings, playerColor });
        }

        // Update opponent's rating display
        let updatedOpponent = currentOpponent;
        if (ratings && playerColor && currentOpponent) {
          const opponentRating = playerColor === 'white' ? ratings.black : ratings.white;
          updatedOpponent = { ...currentOpponent, rating: opponentRating.newRating };
          log.info('Multiplayer', 'onGameEnd → opponent rating updated', {
            opponentOld: opponentRating.oldRating,
            opponentNew: opponentRating.newRating,
          });
        }

        // Track timeout and abandonment events
        const myResult = result === 'draw' ? undefined : result === playerColor ? 'win' as const : 'loss' as const;
        if (reason === 'timeout' && myResult) {
          trackGameTimeout(variant, timeControl ?? 'none', myResult);
        }
        if (reason === 'disconnection' && myResult) {
          trackGameAbandoned(variant, myResult);
        }

        set({
          status: 'ended',
          // Clear any optimistic snapshot so it can't leak into the next game
          // (and wrongly mute its first opponent-move sound / hijack a game:error).
          preMoveBoardState: null,
          preMoveSentAt: null,
          gameResult: result,
          gameEndReason: reason,
          clockState: null,
          sessionScore: sessionScore || { white: 0, black: 0 },
          canRematch: canRematch || false,
          ...REMATCH_OFFER_RESET,
          // Le trattative muoiono con la partita: una barra Accetta/Rifiuta
          // che sopravvive alla fine non ha piu' niente da accettare.
          drawOffered: false,
          ...PATTA_BUDGET_RESET,
          opponentOfferedDraw: false,
          drawDeclinedNotice: false,
          ...TAKEBACK_RESET,
          // Two-leg match: keep the aggregate score up to date. The countdown
          // itself arrives separately (match:return-pending).
          ...(match
            ? {
                matchLegIndex: match.legIndex,
                matchTotalLegs: match.totalLegs,
                matchComplete: match.complete,
                matchScore: toPlayerMatchScore(match.score),
                // The match is over (last leg played, or lost by walkover):
                // drop any countdown still on screen.
                ...(match.complete
                  ? { returnLegStartsAt: null, returnLegReady: false, matchReadyUserIds: [] }
                  : {}),
              }
            : MATCH_RESET),
          opponent: updatedOpponent,
          // A disconnection-forfeit win ends the game while the opponent-disconnected
          // banner is still up; clear it so it doesn't linger behind the game-over overlay.
          isOpponentDisconnected: false,
          opponentDisconnectedColor: null,
          graceTimeRemaining: null,
        });
      },
      onGameAborted: (data) => {
        log.info('Multiplayer', 'onGameAborted', { role: data.role, reason: data.reason });
        // A matchmaking game was voided because a player left before the first
        // real move: no result, no rating. Tear down all in-game state first.
        set({
          roomId: null,
          boardState: null,
          preMoveBoardState: null,
          preMoveSentAt: null,
          lastMove: null,
          gameResult: null,
          gameEndReason: null,
          primaMossaScadeAt: null,
          primaMossaEsito: null,
          openingPlyCount: 0,
          /* Una partita annullata non lascia in giro la sua posizione: la
             prossima potrebbe non essere composta. */
          startBoard: null,
          clockState: null,
          isOpponentDisconnected: false,
          opponentDisconnectedColor: null,
          graceTimeRemaining: null,
          drawOffered: false,
          ...PATTA_BUDGET_RESET,
          opponentOfferedDraw: false,
          drawDeclinedNotice: false,
          ...TAKEBACK_RESET,
          ...REMATCH_OFFER_RESET,
        });
        if (data.role === 'stay') {
          // Remaining player: show the notice and auto re-queue with the same
          // variant + time control (server-provided, falling back to the last game's).
          const resolvedVariant = data.variant ? toGameVariant(data.variant) : get().variant;
          const tc =
            (data.timeControl as TimeControlKey | undefined) ??
            get().timeControl ??
            get().searchTimeControl;
          /*
           * L'avversario che non ha mosso e l'avversario che se n'e' andato
           * sono due cose diverse, e finora si raccontavano con la stessa
           * frase. Ora il server manda il motivo vero.
           */
          set({
            status: 'connected',
            variant: resolvedVariant,
            abortNotice:
              data.reason === REASON_NO_FIRST_MOVE
                ? 'game.aborted.opponentDidNotMove'
                : 'game.aborted.opponentLeft',
          });
          /*
           * …e si riapre un tavolo sulla stessa cadenza, così chi non ha fatto
           * niente di male torna in coda senza premere nulla.
           *
           * Ma SOLO se in coda ci si era davvero. Il commento di prima diceva
           * che `game:aborted` riguarda i soli tavoli rapidi: non era vero già
           * allora — la scadenza della prima mossa annulla qualunque stanza
           * fuori dalle competizioni — e ora che la scadenza copre anche sfide
           * dirette e rivincite sarebbe il percorso comune. Chi giocava una
           * sfida diretta si ritrovava nella coda del matchmaking, a giocare
           * una partita che non aveva chiesto. Lo decide il server
           * (`room.matchmaking`), non un'euristica qui.
           */
          if (data.requeue && tc && tc !== 'none' && tc in TIME_CONTROL_PRESETS) {
            void get().createQuickTable(getVariantString(resolvedVariant), tc as TimeControlPreset);
          }
        } else {
          // The player who left simply returns to the lobby — no loss is shown.
          set({ status: 'connected', abortNotice: null });
        }
      },
      onGameError: (message, code) => {
        // game:error is a shared, untyped channel (chat rate-limit, server-restart
        // broadcast, validation, …). Only a MOVE_REJECTED error means the server
        // rejected the move we just applied optimistically, so only then do we roll
        // the board back — otherwise an unrelated error would revert an accepted
        // move. Arena/tournament games flow through this same callback, so their
        // rollback is dispatched here too; gate on the store being the ACTIVE game
        // context AND holding a pending optimistic move so a stale snapshot can
        // never hijack the wrong game's board.
        log.warn('Multiplayer', 'onGameError', { code, message, hadOptimistic: get().preMoveBoardState !== null });
        /*
         * Il limite a raffica della chat: lo dice la chat, in qualunque partita.
         * Passando dall'errore di partita finiva nell'avviso generico («Qualcosa
         * non ha funzionato») della partita normale, e in torneo e in arena — che
         * quell'errore non lo leggono — in nessun avviso.
         */
        if (code === CHAT_RATE_LIMITED) {
          useChatStore.getState().segnalaLimite();
          return;
        }
        /*
         * La stanza che teneva viva la chat non c'e' piu'. E' il messaggio gia' in
         * volo quando la finestra si chiude: l'avviso vero arriva da
         * `game:chat-closed`, questo e' il ritardatario. Lo dice la chat e non
         * l'avviso di partita, che a fine partita sta dietro l'overlay — e che in
         * torneo e in arena nessuno legge.
         */
        if (code === CHAT_ROOM_CLOSED) {
          useChatStore.getState().chiudiChat(null);
          return;
        }
        if (code === MOVE_REJECTED) {
          const tournament = useTournamentStore.getState();
          if (
            (tournament.status === 'playing' || tournament.status === 'match-found') &&
            tournament.preMoveBoardState
          ) {
            log.warn('Multiplayer', 'MOVE_REJECTED rollback (tournament)', { message });
            tournament.rollbackOptimisticMove(message);
            return;
          }
          const arena = useArenaStore.getState();
          if (
            (arena.status === 'playing' || arena.status === 'matchFound' || arena.status === 'gameEnd') &&
            arena.preMoveBoardState
          ) {
            log.warn('Multiplayer', 'MOVE_REJECTED rollback (arena)', { message });
            arena.rollbackOptimisticMove(message);
            return;
          }
          const { preMoveBoardState } = get();
          if (preMoveBoardState) {
            log.warn('Multiplayer', 'MOVE_REJECTED rollback (multiplayer)', { message });
            set({ boardState: preMoveBoardState, preMoveBoardState: null, preMoveSentAt: null, error: message });
            return;
          }
        }
        // Non-move error (or a move rejection with no pending optimistic move):
        // surface it without touching any board. Draw-offer rejections don't reach
        // here (they use the emit ack), so this only ever affects moves.
        log.warn('Multiplayer', 'onGameError surfaced without rollback', { code, message });
        set({ error: message });
      },
      onClockSync: (clock) => {
        /* `ClockSyncEvent` il `roomId` ce l'ha da sempre (`room.getClockState`):
           qui non lo si guardava, ed era l'altra meta' del difetto — l'orologio
           si applicava alla damiera a cui le mosse venivano negate. */
        const destinatario = destinatarioPerStanza(clock.roomId, get().roomId);
        const tournamentStatus = useTournamentStore.getState().status;
        // `match-found` compreso: l'orologio del server puo' gia' girare prima
        // che il giocatore entri in partita. Vedi la nota su onGameEnd.
        if (
          destinatario === 'torneo' ||
          (destinatario === null && (tournamentStatus === 'playing' || tournamentStatus === 'match-found'))
        ) {
          useTournamentStore.getState().syncClock(clock);
          return;
        }
        const arenaStatus = useArenaStore.getState().status;
        if (destinatario === 'arena' || (destinatario === null && arenaStatus === 'playing')) {
          useArenaStore.getState().syncClock(clock);
          return;
        }
        if (destinatario === 'nessuno') return;
        get().syncClock(clock);
      },
      // Lobby table list, kept live between snapshots. Registered here because
      // this is where every socket callback in the app is wired; the state
      // itself lives in lobby-store.
      onLobbyTableAdded: (table) => {
        useLobbyStore.getState().addTable(table);
      },
      onLobbyTableRemoved: ({ tableId, reason }) => {
        const lobby = useLobbyStore.getState();
        const wasMine = lobby.myTableId === tableId;
        lobby.removeTable(tableId);
        // Our own table went away without us cancelling it — it either became a
        // game (game:start is on its way) or expired. Either way we are no
        // longer queued, so drop the 'searching' status the banner reads.
        if (wasMine && get().status === 'searching') {
          set({ status: 'connected', searchTimeControl: null, searchStartTime: null });
        }
        /*
         * La scadenza va DETTA. `reason` arriva dal server da sempre — `'expired'`
         * per la scopa del TTL — e qui non veniva nemmeno estratto: dopo dieci
         * minuti d'attesa `QueuedTableBanner` spariva e non restava niente a
         * spiegare perché. Chi aspettava non poteva distinguere «scaduto» da «si
         * è rotto qualcosa», e la reazione naturale (restare a guardare) è quella
         * che non porta a giocare.
         *
         * Solo per il PROPRIO tavolo: le scadenze degli altri sono righe che
         * scompaiono da un elenco, non notizie. E solo per `'expired'`:
         * `'started'` porta un `game:start`, `'cancelled'` è un gesto di chi
         * legge.
         */
        if (wasMine && reason === 'expired') {
          set({ abortNotice: 'lobby.tableExpired' });
        }
      },
      onLobbyCounts: (counts) => {
        // Chi è collegato non è roba della lobby: lo legge anche la hero.
        useOnlineStore.getState().setCounts(counts);
      },
      // Registered here and ONLY here: initSocket merges callbacks by key, so a
      // second store registering its own onGameChat would silently replace this
      // one. The chat itself lives in chat-store, which every mode shares.
      onGameChat: (message) => {
        useChatStore.getState().receiveMessage(message);
      },
      // Registrato qui per la stessa ragione della chat qui sopra, e vale per
      // tutte e tre le modalità: `connect()` parte per ogni utente autenticato,
      // quindi questa c'è anche per chi entra dritto in arena o in torneo.
      onGameSpectators: (data) => {
        useSpectatorsStore.getState().receive(data);
      },
      onOpponentPresence: ({ roomId, left }) => {
        useChatStore.getState().setOpponentLeft(roomId, left);
      },
      // Registrata qui come quella sopra, e come quella vale per tutte e tre le
      // modalità: la chat vive nel suo store, non in questo.
      onChatWindow: ({ roomId, closesAt }) => {
        const chat = useChatStore.getState();
        if (closesAt === null) chat.chiudiChat(roomId);
        else chat.segnalaChiusuraChat(roomId, closesAt);
      },
      onOpponentJoined: (data) => {
        // White player receives this when black joins in private rooms
        set({
          opponent: data.username ? { username: data.username } : undefined,
        });
      },
      onDrawOffered: (data) => {
        log.info('Multiplayer', 'onDrawOffered', { roomId: data?.roomId });
        // Per stanza quando il filo la dice, come `onGameEnd`.
        const destinatario = destinatarioPerStanza(data?.roomId, get().roomId);
        /* La stanza di nessuno si scarta QUI e non in coda ai rami: cosi' il
           segnale sotto sta su un percorso solo, invece di essere ripetuto in
           tutti e tre. ('nessuno' non poteva comunque entrare in nessun ramo.) */
        if (destinatario === 'nessuno') {
          log.warn('Multiplayer', 'onDrawOffered scartato: stanza di nessuno', { roomId: data?.roomId });
          return;
        }
        /*
         * Il segnale sensoriale, prima dello smistamento perche' e' lo stesso per
         * tutte e tre le schermate.
         *
         * La proposta si annunciava SOLO cambiando la barra dei comandi, cioe'
         * con un'animazione CSS — e chi stava guardando la damiera per muovere
         * non aveva modo di accorgersene: muoveva, e la mossa la rifiutava. E' la
         * coppia identica delle altre due richieste che arrivano da un avversario
         * (sfida e rivincita in arrivo): la stessa notizia merita la stessa faccia,
         * e dal 2 novembre 2026 anche lo stesso suono — `richiesta`, che e' il loro,
         * non quello dell'inizio partita che si riusava per mancanza di un file.
         *
         * Senza `onceForKey`, perche' una SECONDA proposta nella stessa stanza —
         * dopo un rifiuto — deve suonare di nuovo.
         *
         * I due interruttori dell'utente sono gia' dentro `playSound` e `vibrate`:
         * qui non si filtra.
         */
        playSound('richiesta');
        vibrate([120, 60, 120]);
        const tournamentStatus = useTournamentStore.getState().status;
        const arenaStatus = useArenaStore.getState().status;
        if (destinatario === 'torneo' || (destinatario === null && tournamentStatus === 'playing')) {
          useTournamentStore.setState({ opponentOfferedDraw: true });
          return;
        }
        if (destinatario === 'arena' || (destinatario === null && arenaStatus === 'playing')) {
          useArenaStore.setState({ opponentOfferedDraw: true });
          return;
        }
        set({ opponentOfferedDraw: true });
      },
      onDrawDeclined: (data) => {
        log.info('Multiplayer', 'onDrawDeclined', { roomId: data?.roomId });
        const destinatario = destinatarioPerStanza(data?.roomId, get().roomId);
        const tournamentStatus = useTournamentStore.getState().status;
        const arenaStatus = useArenaStore.getState().status;
        if (destinatario === 'nessuno') return;
        if (destinatario === 'torneo' || (destinatario === null && tournamentStatus === 'playing')) {
          const ts = useTournamentStore.getState();
          useTournamentStore.setState({
            drawOffered: false,
            opponentOfferedDraw: false,
            drawDeclinedNotice: ts.drawOffered,
          });
          return;
        }
        if (destinatario === 'arena' || (destinatario === null && arenaStatus === 'playing')) {
          const as = useArenaStore.getState();
          useArenaStore.setState({
            drawOffered: false,
            opponentOfferedDraw: false,
            drawDeclinedNotice: as.drawOffered,
          });
          return;
        }
        // Only the player who OFFERED gets told it was refused. The server
        // broadcasts `game:draw-declined` to the whole room in two cases — a
        // move that implicitly kills the offer, and the end of the game — so
        // `drawOffered` must be read BEFORE it is cleared (the same
        // `wasRequester` trick `onTakebackAccepted` uses just below). The
        // game-over case is excluded outright: an ACCEPTED draw ends the game
        // and would otherwise arrive as "your draw was refused".
        const wasOfferer = get().drawOffered && get().status === 'playing';
        set({ drawOffered: false, opponentOfferedDraw: false, drawDeclinedNotice: wasOfferer });
      },
      // Takeback (casual online only — never routed to tournament/arena stores).
      onTakebackOffered: (data) => {
        log.info('Multiplayer', 'onTakebackOffered', { toPly: data.toPly, plies: data.plies });
        set({
          opponentRequestedTakeback: true,
          pendingTakebackPlies: data.plies,
          pendingTakebackToPly: data.toPly,
        });
      },
      onTakebackDeclined: () => {
        log.info('Multiplayer', 'onTakebackDeclined');
        set({
          takebackOffered: false,
          opponentRequestedTakeback: false,
          pendingTakebackPlies: null,
          pendingTakebackToPly: null,
          takebackDeclinedNotice: true, // requester banner; auto-clears (see UI)
        });
      },
      onTakebackAccepted: (data) => {
        log.info('Multiplayer', 'onTakebackAccepted', { toPly: data.toPly });
        // The server broadcasts this to the whole room, so BOTH players run this
        // handler. Only the requester (who still has a pending offer) should see the
        // "your takeback was accepted" banner — the acceptor already sees the board
        // roll back. Read takebackOffered BEFORE TAKEBACK_RESET clears it.
        const wasRequester = get().takebackOffered;
        // Authoritative rolled-back board + clock from the server.
        set({
          boardState: data.state,
          lastMove: data.state.moveHistory.at(-1) ?? null,
          /*
           * Il conto della PRIMA mossa si spegne qui, e non e' il flag che la nota
           * su `primaMossaScadeAt` sconsiglia: la sua visibilita' si deriva dalla
           * damiera (`moveHistory.length <= openingPlyCount`), e un takeback fino
           * alla posizione di partenza rende vera di nuovo quella condizione — il
           * conto ricomparirebbe a meta' partita, gia' a zero. Dopo un rifai-mossa
           * la finestra della prima mossa e' chiusa per sempre in questa partita, ed
           * e' esattamente quello che risponde il server (`postiCheHannoMosso`).
           */
          primaMossaScadeAt: null,
          primaMossaEsito: null,
          ...TAKEBACK_RESET,
          takebackAcceptedNotice: wasRequester, // after the spread → wins
        });
        if (data.clock) get().syncClock(data.clock);
        // syncFromExternal does NOT exit review, so the requester (who was scrolled
        // back) must return to the live board. Any premove is stale now too.
        const gs = useGameStore.getState();
        gs.reviewToIndex(null);
        gs.clearPremove();
      },
      onRematchRequested: () => {
        log.info('Multiplayer', 'rematch requested by opponent');
        // Opponent wants a rematch
        set({ opponentWantsRematch: true });
      },
      onRematchDeclined: () => {
        log.info('Multiplayer', 'rematch declined by opponent');
        // The opponent said no. The server DELETED the room, so there is nothing
        // left to offer — `canRematch: false` is what hides the button, and the
        // notice is what finally explains why it went away.
        set({
          ...REMATCH_OFFER_RESET,
          canRematch: false,
          rematchNotice: 'declined',
        });
      },
      onRematchExpired: () => {
        log.info('Multiplayer', 'rematch offer expired');
        // Nobody refused: the offer just timed out. The room is still there, so
        // `canRematch` is deliberately left alone and the button stays pressable
        // for a second try — the one thing a refusal must not allow.
        set({
          ...REMATCH_OFFER_RESET,
          rematchNotice: 'expired',
        });
      },
      onRematchStart: (data) => {
        log.info('Multiplayer', 'rematch accepted — starting new game', { color: data.color, roomId: data.roomId });
        // Rematch is starting with swapped colors
        trackRematchAccepted();
        // Clear any global rematch-invite banner (this player may have accepted
        // the rematch from outside the game via the invite flow).
        useOnlineStore.getState().setIncomingRematch(null);
        set({
          status: 'playing',
          // Repopulate roomId — a player who had LEFT the game cleared it on
          // reset(); without this they couldn't make moves in the new game.
          roomId: data.roomId,
          // Restore the opponent card. A player who LEFT cleared `opponent` on
          // reset() → take it from the payload; a player still in-room keeps
          // their existing (complete) opponent so a transient server-side
          // profile-lookup failure can never blank out the flag/avatar.
          opponent: get().opponent ?? data.opponent ?? null,
          boardState: data.state,
          preMoveBoardState: null,
          preMoveSentAt: null,
          playerColor: data.color,
          lastMove: null,
          gameResult: null,
          gameEndReason: null,
          // A rematch is a fresh game — reset the start time so the banner
          // re-arms (triggerKey) and duration tracking restarts.
          gameStartedAtMs: Date.now(),
          // Stanza riusata, partita nuova: `startClock()` sul server ha riscritto
          // `clockArmedAt`, quindi anche la scadenza della prima mossa riparte.
          primaMossaScadeAt: data.primaMossa ? Date.now() + data.primaMossa.restaMs : null,
          primaMossaEsito: data.primaMossa?.esito ?? null,
          openingPlyCount: data.opening?.moves?.length ?? 0,
          /* La rivincita di una partita composta riparte dalla STESSA posizione
             (la stanza riclona la sua `startBoard`), e la manda di nuovo. */
          startBoard: data.startBoard ?? null,
          drawnOpening: annuncioApertura(data.opening),
          sessionScore: data.sessionScore,
          /*
           * Di che partita si tratta. Chi era uscito dalla schermata di fine
           * partita ha azzerato il suo stato, e questi due campi erano i soli
           * che nessuno gli restituiva: l'intestazione diceva «Internazionale»
           * e la cadenza «—» su una damiera di un'altra variante, il punteggio
           * della card veniva dal bucket sbagliato, e su russa o brasiliana la
           * lista mosse perdeva la notazione algebrica.
           *
           * Appiccicosi come in `onGameStart`: un campo assente (server
           * vecchio) non sovrascrive e non azzera.
           */
          ...(data.variant ? { variant: toGameVariant(data.variant) } : {}),
          ...(data.timeControl ? { timeControl: data.timeControl } : {}),
          ...REMATCH_OFFER_RESET,
          canRematch: false,
          // A rematch of a drawn-opening challenge starts a brand-new two-leg
          // match (new opening, score back to 0-0).
          ...(data.match
            ? {
                matchLegIndex: data.match.legIndex,
                matchTotalLegs: data.match.totalLegs,
                matchComplete: data.match.complete,
                matchScore: toPlayerMatchScore(data.match.score),
                returnLegStartsAt: null,
                returnLegReady: false,
                matchReadyUserIds: [],
              }
            : MATCH_RESET),
          drawOffered: false,
          ...PATTA_BUDGET_RESET,
          opponentOfferedDraw: false,
          drawDeclinedNotice: false,
          ...TAKEBACK_RESET,
          isOpponentDisconnected: false,
          opponentDisconnectedColor: null,
          graceTimeRemaining: null,
        });
        // A rematch reuses the SAME server room, so the conversation continues:
        // setChatContext only wipes the transcript when the roomId changes. A
        // player who had LEFT (and so cleared the context on reset) gets it back.
        useChatStore.getState().setChatContext({
          roomId: data.roomId,
          playerColor: data.color,
          canChat: true,
          source: 'online',
        });
        // Initialize clock if provided
        if (data.clock) {
          get().initClock(data.clock);
        }
        // A rematch is a fresh game. syncFromExternal preserves review/selection across
        // re-syncs, so a player who was scrolled back through the finished game must be
        // returned to the live board — otherwise the new game renders the old position.
        const gs = useGameStore.getState();
        gs.reviewToIndex(null);
        gs.clearPremove();
      },
      onRematchInvite: (data) => {
        log.info('Multiplayer', 'rematch invite received', { from: data.fromUsername, roomId: data.roomId });
        // Surfaced globally (like a direct challenge) so a player who left the
        // game-over screen can still accept a rematch from anywhere.
        useOnlineStore.getState().setIncomingRematch({
          roomId: data.roomId,
          fromUserId: data.fromUserId,
          fromUsername: data.fromUsername,
          fromRating: data.fromRating,
          variant: data.variant,
          timeControl: data.timeControl,
          expiresAt: data.expiresAt,
        });
      },
      onRematchInviteExpired: (data) => {
        log.info('Multiplayer', 'rematch invite expired', { roomId: data.roomId });
        const current = useOnlineStore.getState().incomingRematch;
        if (current?.roomId === data.roomId) {
          useOnlineStore.getState().setIncomingRematch(null);
        }
      },

      // ===== Two-leg match ("andata e ritorno") =====
      // Leg 1 is over: the return leg starts by itself at `startsAt`. The client
      // stays on the game-over screen with a countdown instead of a rematch panel.
      onMatchReturnPending: (data) => {
        log.info('Multiplayer', 'match:return-pending', { roomId: data.roomId, startsAt: data.startsAt });
        if (get().roomId !== data.roomId) return;
        set({
          returnLegStartsAt: data.startsAt,
          returnLegReady: false,
          matchReadyUserIds: [],
          matchLegIndex: data.legIndex,
          matchTotalLegs: data.totalLegs,
          matchComplete: false,
          matchScore: toPlayerMatchScore(data.score),
          canRematch: false,
        });
      },
      onMatchReadyState: (data) => {
        if (get().roomId !== data.roomId) return;
        const myId = useAuthStore.getState().user?.id;
        set({
          matchReadyUserIds: data.readyUserIds,
          returnLegReady: !!myId && data.readyUserIds.includes(myId),
        });
      },
      // The return leg itself: same shape as a rematch, but never negotiated.
      onMatchLegStart: (data) => {
        log.info('Multiplayer', 'match:leg-start', { roomId: data.roomId, color: data.color, leg: data.match?.legIndex });
        set({
          status: 'playing',
          roomId: data.roomId,
          opponent: get().opponent ?? data.opponent ?? null,
          boardState: data.state,
          preMoveBoardState: null,
          preMoveSentAt: null,
          playerColor: data.color,
          lastMove: null,
          gameResult: null,
          gameEndReason: null,
          // A new leg is a new game: re-arm the opening banner and duration tracking.
          gameStartedAtMs: Date.now(),
          primaMossaScadeAt: data.primaMossa ? Date.now() + data.primaMossa.restaMs : null,
          primaMossaEsito: data.primaMossa?.esito ?? null,
          openingPlyCount: data.opening?.moves?.length ?? 0,
          /* Oggi sempre `null` — una sfida a due manche non e' mai composta —
             e scritto comunque, per non lasciare il campo a quello di prima. */
          startBoard: data.startBoard ?? null,
          drawnOpening: annuncioApertura(data.opening),
          sessionScore: data.sessionScore,
          // Come nella rivincita: la stanza e' la stessa, lo stato del client no.
          ...(data.variant ? { variant: toGameVariant(data.variant) } : {}),
          ...(data.timeControl ? { timeControl: data.timeControl } : {}),
          ...REMATCH_OFFER_RESET,
          canRematch: false,
          matchLegIndex: data.match?.legIndex ?? 2,
          matchTotalLegs: data.match?.totalLegs ?? 2,
          matchComplete: false,
          matchScore: toPlayerMatchScore(data.match?.score),
          returnLegStartsAt: null,
          returnLegReady: false,
          matchReadyUserIds: [],
          drawOffered: false,
          ...PATTA_BUDGET_RESET,
          opponentOfferedDraw: false,
          drawDeclinedNotice: false,
          ...TAKEBACK_RESET,
          isOpponentDisconnected: false,
          opponentDisconnectedColor: null,
          graceTimeRemaining: null,
        });
        if (data.clock) {
          get().initClock(data.clock);
        }
        // Return the local board to live — the player was reviewing the finished leg.
        const gs = useGameStore.getState();
        gs.reviewToIndex(null);
        gs.clearPremove();
      },

      // Disconnection/Reconnection handlers (routed like onGameStart/onGameEnd)
      onPlayerDisconnected: (data) => {
        log.info('Multiplayer', 'onPlayerDisconnected', { roomId: data.roomId, color: data.color, graceTimeMs: data.graceTimeMs });
        const tournamentStatus = useTournamentStore.getState().status;
        const arenaStatus = useArenaStore.getState().status;
        const disconnectState = {
          isOpponentDisconnected: true,
          opponentDisconnectedColor: data.color,
          graceTimeRemaining: data.graceTimeMs,
          // Sempre scritti, mai ereditati: un avviso nuovo non deve portarsi
          // dietro le parole di quello prima.
          opponentMaiArrivato: data.maiArrivato === true,
          opponentDecideLOrologio: data.decideLOrologio === true,
        };
        /*
         * La delega viene prima della guardia, come gia fa `onPlayerReconnected`.
         * Durante una partita d'arena (o di torneo) questo store NON e in
         * 'playing': `onGameStart` delega e ritorna prima del `set`, e
         * `applyReconnectResult` fa lo stesso al rientro. Con la guardia davanti
         * scattava sempre e i due rami sotto erano irraggiungibili: chi giocava in
         * arena non ha mai saputo che l'avversario era caduto.
         */
        if (tournamentStatus === 'playing') {
          if (avvisoDiUnAltraStanza(data.roomId, useTournamentStore.getState().currentRoomId)) {
            log.info('Multiplayer', 'onPlayerDisconnected dropped (altra stanza, torneo)', { roomId: data.roomId });
            return;
          }
          useTournamentStore.setState(disconnectState);
          return;
        }
        if (arenaStatus === 'playing') {
          if (avvisoDiUnAltraStanza(data.roomId, useArenaStore.getState().currentRoomId)) {
            log.info('Multiplayer', 'onPlayerDisconnected dropped (altra stanza, arena)', { roomId: data.roomId });
            return;
          }
          useArenaStore.setState(disconnectState);
          return;
        }

        // Qui la partita e di questo store, e la guardia serve davvero: un
        // disconnect in coda dalla partita precedente non deve accendere il
        // periodo di grazia su una schermata che non sta giocando.
        const { status } = get();
        if (status !== 'playing') {
          log.info('Multiplayer', 'onPlayerDisconnected dropped (status != playing)', { status });
          return;
        }
        if (avvisoDiUnAltraStanza(data.roomId, get().roomId)) {
          log.info('Multiplayer', 'onPlayerDisconnected dropped (altra stanza)', { roomId: data.roomId, corrente: get().roomId });
          return;
        }
        set(disconnectState);
      },
      onPlayerReconnected: (data) => {
        log.info('Multiplayer', 'onPlayerReconnected', { roomId: data.roomId, color: data.color });
        const tournamentStatus = useTournamentStore.getState().status;
        const arenaStatus = useArenaStore.getState().status;
        const reconnectState = {
          isOpponentDisconnected: false,
          opponentDisconnectedColor: null,
          graceTimeRemaining: null,
        };
        /* Anche il rientro si filtra: quello dell'avversario di una partita
           vecchia spegnerebbe la scritta di un avversario nuovo davvero caduto. */
        if (tournamentStatus === 'playing') {
          if (avvisoDiUnAltraStanza(data.roomId, useTournamentStore.getState().currentRoomId)) return;
          useTournamentStore.setState(reconnectState);
          return;
        }
        if (arenaStatus === 'playing') {
          if (avvisoDiUnAltraStanza(data.roomId, useArenaStore.getState().currentRoomId)) return;
          useArenaStore.setState(reconnectState);
          return;
        }
        if (avvisoDiUnAltraStanza(data.roomId, get().roomId)) return;
        set(reconnectState);
      },
      onGameRejoined: (data) => {
        log.info('Multiplayer', 'onGameRejoined', { roomId: data.roomId, color: data.color, variant: data.variant, tournamentId: data.tournamentId, arenaId: data.arenaId });
        /*
         * Le partite che non sono nostre si consegnano, non si tengono.
         *
         * `rejoinGame` (la porta di `check-active`, qui sopra) questi due rami
         * li ha da sempre; questa porta — il rientro automatico dentro il
         * periodo di grazia — no, e il payload non portava nemmeno di chi fosse
         * la partita. Cosi' bastava ricaricare la pagina durante un turno per
         * ritrovarsi sulla schermata online: `App` ci porta perche' il
         * multiplayer passa a 'playing', e poi non ce ne toglie piu' (la
         * guardia della navigazione considera `online-game` una schermata di
         * partita legittima, e non la corregge).
         *
         * Da li' in poi quella schermata mente su tutto: i bottoni parlano
         * della stanza del MULTIPLAYER, che al turno successivo non viene
         * aggiornata — `onGameStart` delega al torneo e ritorna prima — e
         * l'avversario resta senza nome, perche' il nome arriva in
         * `tournament:match`, che finisce nell'altro store.
         */
        if (data.tournamentId) {
          log.info('Multiplayer', 'onGameRejoined → delegato al torneo', { tournamentId: data.tournamentId });
          useTournamentStore.getState().rientraInPartita({
            tournamentId: data.tournamentId,
            roomId: data.roomId,
            state: data.state,
            color: data.color,
            clock: data.clock ?? null,
            /* Come nel gemello di `check-active` qui sopra: nessun numero
               inventato, e l'avatar che prima il rientro perdeva. */
            opponent: data.opponent?.oddsUserName
              ? {
                  id: data.opponent.oddsUserId ?? '',
                  username: data.opponent.oddsUserName,
                  rating: data.opponent.oddsRating ?? null,
                  avatar: data.opponent.oddsAvatar ?? null,
                }
              : null,
            chat: data.chat,
            timeControl: data.timeControl,
            opening: data.opening,
            /* La manche: vedi `campoManche` sul server, e `rientraInPartita`. */
            manche: data.manche,
          });
          return;
        }
        if (data.arenaId) {
          log.info('Multiplayer', 'onGameRejoined → delegato all arena', { arenaId: data.arenaId });
          useArenaStore.getState().rejoinGame({
            arenaId: data.arenaId,
            roomId: data.roomId,
            state: data.state,
            color: data.color,
            clock: data.clock ?? null,
            timeControl: data.timeControl ?? null,
            opponent: data.opponent?.oddsUserName
              ? {
                  id: data.opponent.oddsUserId,
                  username: data.opponent.oddsUserName,
                  rating: data.opponent.oddsRating,
                  avatar: data.opponent.oddsAvatar ?? null,
                }
              : null,
            chat: data.chat,
            openingPlyCount: data.openingPlyCount,
            opening: data.opening,
            /* La manche: vedi `campoManche` sul server, e `rejoinGame`. */
            manche: data.manche,
          });
          return;
        }
        // `game-reconnected` on the next line already IS this event; the old
        // `matchmaking-rejoined` twin only ever double-counted the same moment,
        // and with the queue gone its name no longer described anything.
        //
        // Si conta un RIENTRO, non un riallineamento. Da quando il server rifa'
        // il `join` (e manda questo evento) a ogni connessione con una partita
        // viva — non piu' solo dentro il periodo di grazia — la stessa stanza
        // puo' ripresentarsi senza che il giocatore sia mai andato via: contarla
        // gonfierebbe la metrica proprio dove abbiamo aggiunto una riparazione.
        const eraAltrove = get().roomId !== data.roomId || get().status !== 'playing';
        if (eraAltrove) trackGameReconnected(data.variant || 'international');
        set({
          status: 'playing',
          roomId: data.roomId,
          boardState: data.state,
          preMoveBoardState: null,
          preMoveSentAt: null,
          playerColor: data.color,
          lastMove: data.state.moveHistory[data.state.moveHistory.length - 1] || null,
          gameResult: null,
          gameEndReason: null,
          isOpponentDisconnected: false,
          opponentDisconnectedColor: null,
          graceTimeRemaining: null,
          ...(data.variant ? { variant: toGameVariant(data.variant || 'international') } : {}),
          ...(data.timeControl ? { timeControl: data.timeControl as TimeControlKey } : {}),
          /* L'apertura e il suo conto, come in check-active: senza, il ricarico
             toglieva intestazione e grigi della scheda Mosse. */
          openingPlyCount: data.openingPlyCount ?? data.opening?.moves?.length ?? 0,
          /* Come in check-active, e per la stessa ragione. */
          startBoard: data.startBoard ?? null,
          drawnOpening: get().roomId === data.roomId
            ? aperturaStabile(get().drawnOpening, annuncioApertura(data.opening))
            : annuncioApertura(data.opening),
        });
        // La conversazione com'era prima della caduta. Il contesto non si tocca:
        // è lo stesso di prima (stessa room), e se non c'è ancora — riavvio
        // dell'app — arriva subito dopo da check-active, che ripristina di nuovo.
        if (data.chat) useChatStore.getState().restoreMessages(data.chat);
        // Sync clock if provided
        if (data.clock) {
          get().syncClock(data.clock);
        }
      },

      // Tournament updates (forwarded to tournament store). Gli eventi della
      // STANZA del torneo vengono da una definizione sola, condivisa col
      // tournament-store: registrati da qui, arrivano anche a chi guarda una
      // scheda senza esserne iscritto (avvio, turni, annullamento...).
      ...callbackDellaStanzaTorneo(),
      onTournamentMatch: (data) => {
        useTournamentStore.getState().setTournamentMatch(data);
      },
      onTournamentError: (error) => {
        // Chiave i18n, non la frase del server: le schermate dei tornei rendono
        // `t(error)`. Vedi `utils/tournament-error-key.ts`.
        useTournamentStore.getState().setError(tournamentErrorKeyFor(error));
      },
      onTournamentCreated: () => {
        // Reload tournament list when a new tournament is created
        useTournamentStore.getState().loadTournaments();
      },

      // Challenge callbacks
      onChallengeReceived: (challenge) => {
        log.info('Challenge', 'Incoming challenge received', { from: challenge.fromUsername });
        useOnlineStore.getState().setIncomingChallenge(challenge as unknown as import('./online-store').Challenge);
      },
      onChallengeAccepted: (_challenge) => {
        log.info('Challenge', 'Challenge accepted (awaiting game:start)');
        useOnlineStore.getState().setOutgoingChallenge(null);
        // game:start event from backend will handle navigation
      },
      onChallengeDeclined: (challenge) => {
        log.info('Challenge', 'Challenge declined');
        // Mark as declined so OutgoingChallengeModal can show brief message
        const current = useOnlineStore.getState().outgoingChallenge;
        if (current && current.id === (challenge as { id?: string })?.id) {
          useOnlineStore.getState().setOutgoingChallenge({ ...current, status: 'declined' });
          // Auto-clear after 2s
          setTimeout(() => {
            useOnlineStore.getState().setOutgoingChallenge(null);
          }, 2000);
        } else {
          log.warn('Challenge', 'declined id mismatch — clearing outgoing challenge', { currentId: current?.id, declinedId: (challenge as { id?: string })?.id });
          useOnlineStore.getState().setOutgoingChallenge(null);
        }
      },
      onChallengeCancelled: (_challenge) => {
        log.info('Challenge', 'Challenge cancelled');
        useOnlineStore.getState().setIncomingChallenge(null);
      },

      // Challenge-link callbacks. The game arrives as game:start on both sides;
      // these only keep the two waiting screens honest.
      onLinkChallengeClaimed: (link) => {
        log.info('Challenge', 'Challenge link claimed', { by: link.claimedByUsername });
        const online = useOnlineStore.getState();
        if (online.linkChallenge?.id === link.id) online.setLinkChallenge(link);
      },
      onLinkChallengeReleased: (link) => {
        log.info('Challenge', 'Challenge link released');
        const online = useOnlineStore.getState();
        if (online.linkChallenge?.id === link.id) online.setLinkChallenge(link);
      },
      onLinkChallengeCancelled: (link) => {
        log.info('Challenge', 'Challenge link cancelled');
        const online = useOnlineStore.getState();
        if (online.linkChallenge?.id === link.id) online.setLinkChallenge(null);
        if (online.pendingLinkChallenge?.id === link.id) {
          online.setPendingLinkChallenge(null);
          online.setLinkChallengeNotice('cancelled');
        }
      },
      onLinkChallengeExpired: (link) => {
        log.info('Challenge', 'Challenge link expired');
        const online = useOnlineStore.getState();
        if (online.linkChallenge?.id === link.id) {
          online.setLinkChallenge(null);
          online.setLinkChallengeNotice('expired');
        }
        if (online.pendingLinkChallenge?.id === link.id) {
          online.setPendingLinkChallenge(null);
          online.setLinkChallengeNotice('expired');
        }
      },

      // Keep the online players list live without waiting for the 30s poll.
      onOnlineUpdate: (data) => {
        const online = useOnlineStore.getState();
        switch (data.type) {
          case 'joined':
            // Never list yourself: online:list already excludes the requester, but a
            // stray self 'joined' (e.g. a multi-tab/session-eviction overlap) would
            // otherwise insert the current user into their own online players list.
            if (data.user.userId === useAuthStore.getState().user?.id) break;
            online.addOnlineUser({
              userId: data.user.userId,
              username: data.user.username,
              avatar: data.user.avatar ?? null,
              // Niente 1000 di ripiego: un ospite non ha punteggio, e
              // reintrodurlo qui annullerebbe l'omissione fatta dal server.
              rating: data.user.rating ?? null,
              status: (data.user.status as OnlineUser['status']) ?? 'available',
              isGuest: data.user.isGuest,
            });
            break;
          case 'left':
            online.removeOnlineUser(data.user.userId);
            break;
          case 'status':
            online.updateUserStatus(data.userId, (data.status as OnlineUser['status']) ?? 'available', data.opponent);
            break;
          case 'rating':
            online.punteggiCambiati(data.userIds);
            break;
        }
      },
    };

    // Always update callbacks (even if socket already connected)
    initSocket(socketCallbacks);

    if (isConnected()) {
      set({ status: 'connected' });
      // Still check for active games to rejoin (or a game that ended while away)
      try {
        const result = await checkActiveGame();
        applyReconnectResult(result, set, get);
      } catch (error) {
        log.error('Multiplayer', 'Error checking active game', error);
      }
      return;
    }

    set({ status: 'connecting', error: null });
    log.info('Multiplayer', 'status → connecting');
  },

  // Disconnect from server
  invalidaPresenza: () => {
    fermaTimerScadenzaPresenza();
    if (!get().presenzaScaduta) {
      log.info('Multiplayer', 'presenza scaduta: svuoto tavoli e collegati');
      set({ presenzaScaduta: true });
    }
    // Idempotente di proposito: i due inneschi (socket caduto e dispositivo
    // senza rete) possono arrivare in qualunque ordine, e il secondo non deve
    // trovare porte chiuse.
    useLobbyStore.getState().invalida();
    useOnlineStore.getState().invalida();
  },

  disconnect: () => {
    log.info('Multiplayer', 'disconnect() called', { wasStatus: get().status });
    disconnectSocket();
    set({
      status: 'disconnected',
      roomId: null,
      playerColor: null,
      boardState: null,
    });
  },

  /**
   * Press a quick cadence button: sit at the oldest matching table, or open one
   * and wait.
   *
   * The waiting status is still `'searching'`, deliberately. It is what the
   * arena/tournament guards on `game:start` key off, what routes the app to the
   * board when the game begins, and what tells `isSafeToReload` not to reload.
   * The queue changed shape; what "waiting for an opponent" means to the rest of
   * the app did not.
   */
  createQuickTable: async (variant: string, timeControl: TimeControlPreset) => {
    const { status } = get();
    log.info('Multiplayer', 'createQuickTable()', { variant, timeControl });
    if (status === 'playing') {
      /*
       * `useLobbyStore.setError`, non `set({ error })` di QUESTO store.
       *
       * `PlayOverlay` legge `useLobbyStore.error` (e lo rende con `role="alert"`);
       * l'`error` del multiplayer store non lo legge nessuno lì. Il messaggio
       * finiva quindi in un campo che nessuno mostra, e il tocco su un tavolo
       * sembrava non fare niente. Il percorso è stretto ma reale: la modale «Gioca»
       * resta montata durante la partita (fa parte degli overlay globali), quindi se
       * `game:start` arriva a modale aperta si può ancora toccare un tavolo.
       *
       * Lo stesso ramo di `createQuickTable` era già stato riparato per il caso
       * «non connesso», scrivendo sulla lobby: le tre funzioni ora sono allineate.
       */
      useLobbyStore.getState().setError('online.errors.alreadyInGame');
      return;
    }
    /*
     * ⚠️ Non piu' «solo da 'connected' o 'searching'».
     *
     * 'ended' e' lo stato in cui si finisce dopo una fine partita — compresa
     * quella che il RIENTRO applica quando la partita si e' chiusa mentre si
     * era in secondo piano, con l'app che riapre sulla home. Li' il bottone
     * della cadenza chiamava questa funzione, che usciva scrivendo un errore
     * che nessuno mostra: premere «Gioca» non produceva NIENTE, e continuava a
     * non produrre niente finche' il ponte delle partite concluse non scadeva
     * (cinque minuti). Trovato provando il rientro in un browser vero.
     *
     * Le due sorelle qui accanto — `sitAtLobbyTable` e `createCustomTable` —
     * hanno sempre bloccato solo 'playing': era questa a essere fuori riga.
     *
     * Quel che conta davvero e' avere il socket; e se non c'e', l'errore si
     * scrive DOVE si vede, cioe' nella lobby.
     */
    if (!isConnected()) {
      log.warn('Multiplayer', 'createQuickTable: socket non connesso', { status });
      useLobbyStore.getState().setError('online.errors.nonConnesso');
      return;
    }

    const lobby = useLobbyStore.getState();
    lobby.setBusy(true);
    lobby.setError(null);
    // The caller's variant is authoritative and is mirrored onto the store, not
    // read back out of it: the store's `variant` is whatever the LAST game left
    // there, and reading it here is exactly how a table opened as Italian came
    // out International. Everything downstream (game:start resolution, the
    // abort re-queue) still wants it set, so it is set — just not consulted.
    set({
      status: 'searching',
      variant: toGameVariant(variant),
      searchTimeControl: timeControl,
      searchStartTime: Date.now(),
      error: null,
    });

    try {
      // The raw string goes out as-is: `toGameVariant` falls back to
      // International on anything it does not know, and a silent fallback is
      // the failure this whole change is about. An unknown value must be the
      // server's INVALID_VARIANT, not a different game.
      const result = await quickPlay(variant, timeControl);
      if (result.seated) {
        // The game is already running; game:start flips us to 'playing'.
        log.info('Multiplayer', 'seated at an existing table', { roomId: result.roomId });
        useLobbyStore.getState().setMyTable(null);
      } else {
        useLobbyStore.getState().setMyTable(result.tableId);
      }
    } catch (err) {
      log.error('Multiplayer', 'quickPlay rejected', { timeControl, variant, err });
      set({ status: 'connected', searchTimeControl: null, searchStartTime: null, abortNotice: null });
      useLobbyStore.getState().setError(lobbyErrorKey(err));
    } finally {
      useLobbyStore.getState().setBusy(false);
    }
  },

  /** Open a table with the full option set (the custom-game form). */
  createCustomTable: async (settings: ChallengeSettings) => {
    log.info('Multiplayer', 'createCustomTable()', { variant: settings.variant, timeControl: settings.timeControl });
    if (get().status === 'playing') {
      // Vedi la nota in `createQuickTable`: il messaggio va sulla LOBBY, che è
      // l'unico `error` che `PlayOverlay` legge.
      useLobbyStore.getState().setError('online.errors.alreadyInGame');
      return false;
    }

    const lobby = useLobbyStore.getState();
    lobby.setBusy(true);
    lobby.setError(null);
    set({ status: 'searching', searchTimeControl: null, searchStartTime: Date.now(), error: null });

    try {
      const { tableId } = await createLobbyTable(settings);
      useLobbyStore.getState().setMyTable(tableId);
      return true;
    } catch (err) {
      log.error('Multiplayer', 'createLobbyTable rejected', { err });
      set({ status: 'connected', searchTimeControl: null, searchStartTime: null });
      useLobbyStore.getState().setError(lobbyErrorKey(err));
      return false;
    } finally {
      useLobbyStore.getState().setBusy(false);
    }
  },

  /** Sit at a specific table picked from the list. */
  sitAtLobbyTable: async (tableId: string) => {
    log.info('Multiplayer', 'sitAtLobbyTable()', { tableId });
    if (get().status === 'playing') {
      // Vedi la nota in `createQuickTable`: il messaggio va sulla LOBBY, che è
      // l'unico `error` che `PlayOverlay` legge.
      useLobbyStore.getState().setError('online.errors.alreadyInGame');
      return false;
    }

    const lobby = useLobbyStore.getState();
    lobby.setBusy(true);
    lobby.setError(null);

    try {
      await sitAtTable(tableId);
      // game:start is already on its way; it is what sets 'playing'.
      useLobbyStore.getState().setMyTable(null);
      return true;
    } catch (err) {
      log.error('Multiplayer', 'sitAtTable rejected', { tableId, err });
      useLobbyStore.getState().setError(lobbyErrorKey(err));
      return false;
    } finally {
      useLobbyStore.getState().setBusy(false);
    }
  },

  /** Leave the queue. */
  clearAbortNotice: () => set({ abortNotice: null }),

  cancelMyTable: () => {
    const { status } = get();
    const { myTableId } = useLobbyStore.getState();
    log.info('Multiplayer', 'cancelMyTable()', { status, myTableId });
    cancelLobbyTable(myTableId ?? undefined);
    useLobbyStore.getState().setMyTable(null);
    if (status === 'searching') {
      set({ status: 'connected', searchTimeControl: null, searchStartTime: null, abortNotice: null });
    }
  },

  // Make a move
  makeMove: (move: Move) => {
    log.info('Multiplayer', 'makeMove() called', { from: move.from, to: move.to });
    const { roomId, status, playerColor, boardState } = get();

    if (status !== 'playing' || !roomId || !boardState) {
      log.warn('Multiplayer', 'makeMove ignored', { status, hasRoom: !!roomId, hasBoard: !!boardState });
      return;
    }

    // Check if it's our turn
    const isOurTurn =
      (playerColor === 'white' && boardState.currentTurn === Color.WHITE) ||
      (playerColor === 'black' && boardState.currentTurn === Color.BLACK);

    if (!isOurTurn) {
      log.warn('Multiplayer', 'makeMove ignored: not our turn', { playerColor, currentTurn: boardState.currentTurn });
      set({ error: "It's not your turn" });
      return;
    }

    // Optimistic update: apply the move locally for instant feedback instead of
    // waiting a full network round-trip for the server's game:update echo. The
    // core engine mirrors the server's move logic, so the optimistic board
    // matches what the server will broadcast — reconciliation in onGameUpdate is
    // seamless. Turn/clock gating all derive from boardState, so this also flips
    // the turn immediately. preMoveBoardState lets us roll back if the server
    // rejects the move (game:error). applyMove throws on an illegal move; in that
    // (unexpected, since the UI only submits engine-legal moves) case we just
    // send and let the server be the source of truth.
    try {
      const optimistic = applyMove(boardState, move);
      log.info('Multiplayer', 'optimistic apply', { from: move.from, to: move.to, newTurn: optimistic.currentTurn });
      set({ boardState: optimistic, lastMove: move, preMoveBoardState: boardState, preMoveSentAt: Date.now() });
      // Play move feedback immediately on the optimistic apply rather than waiting
      // for the server's game:update echo — otherwise the sound lags by a full
      // network round-trip. onGameUpdate skips the echo to avoid a double sound.
      playMoveSound(move.isPromotion, undefined, move.captures.length > 0);
      vibrate(40);
    } catch (err) {
      log.warn('Multiplayer', 'optimistic applyMove failed; sending without local apply', {
        from: move.from,
        to: move.to,
        currentTurn: boardState.currentTurn,
        playerColor,
        err,
      });
    }

    sendMove(roomId, move);
  },

  /*
   * Abbandona — e non tace mai.
   *
   * Prima usciva in silenzio fuori dallo stato 'playing', ed era la meta'
   * client del difetto «Abbandona non funziona»: chi rientrava su una partita
   * chiusa mentre era via aveva lo store fermo a uno stato non giocabile, il
   * bottone c'era, e premerlo non faceva assolutamente niente. Adesso lo stato
   * incoerente e' un motivo per RICHIEDERE la verita' al server, non per
   * ignorare il gesto.
   */
  resign: () => {
    log.info('Multiplayer', 'resign() called');
    const { roomId, status } = get();
    if (!roomId) {
      log.warn('Multiplayer', 'resign ignorato: nessuna stanza');
      return;
    }
    if (status !== 'playing') {
      log.warn('Multiplayer', 'resign su stato non giocabile: chiedo lo stato vero al server', { status });
      /*
       * E si RISPONDE al gesto. Se il socket e' giu' lo si dice — premere un
       * bottone e non vedere accadere niente e' il difetto da cui nasce tutto
       * questo lavoro. Se invece il socket c'e', la risposta arriva da sola
       * (fine partita o damiera ripristinata) e un errore sarebbe una bugia.
       */
      if (!isConnected()) set({ error: 'NOT_CONNECTED' });
      void get().resyncGame();
      return;
    }
    void resignGame(roomId).then((risposta) => {
      /*
       * La risposta puo' arrivare quando la stanza non e' piu' la nostra:
       * `confirmLeave` chiama `resign()` e subito dopo `reset()`, e una fine
       * partita in ritardo non deve resuscitare una partita gia' lasciata.
       */
      if (get().roomId !== roomId) {
        log.info('Multiplayer', 'ack di abbandono scartato: stanza cambiata', { roomId });
        return;
      }
      if (risposta.success) return;
      if (risposta.timedOut) {
        /* Nessun ack: quasi sempre un server ancora vecchio, dove l'abbandono
           e' partito lo stesso e la fine partita arriva da `game:end`. Non e'
           un errore da mostrare, ma un motivo per riallinearsi. */
        void get().resyncGame();
        return;
      }
      if (risposta.error === RESIGN_ALREADY_ENDED) {
        if (risposta.ended) {
          log.info('Multiplayer', 'abbandono su partita gia\' finita: applico l\'esito');
          applyReconnectResult(risposta.ended, set, get);
        } else {
          void get().resyncGame();
        }
        return;
      }
      log.warn('Multiplayer', 'abbandono rifiutato', { error: risposta.error });
      set({ error: risposta.error ?? null });
      void get().resyncGame();
    });
  },

  resyncGame: async () => {
    /*
     * ⚠️ A socket giu' NON si interroga.
     *
     * `emettiCheckActive` senza socket risponde `{hasActiveGame:false}` — senza
     * `timedOut`, cioe' indistinguibile da «non hai partite» — e darlo a
     * `applyReconnectResult` RILASCEREBBE una partita viva. Sarebbe il bottone
     * «Riprova» che butta via la partita che deve salvare. Si sveglia il
     * socket: il resync lo fa `onConnect`, che ha gia' tutto.
     */
    if (!isConnected()) {
      log.info('Multiplayer', 'resync: socket giu, sveglio la connessione');
      await risvegliaSocket();
      return;
    }
    try {
      const result = await checkActiveGame();
      applyReconnectResult(result, set, get);
    } catch (error) {
      log.error('Multiplayer', 'resync fallito', error);
    }
  },

  clearError: () => {
    if (get().error !== null) set({ error: null });
  },

  clearPartitaTerminataMentreEriVia: () => {
    if (get().partitaTerminataMentreEriVia) set({ partitaTerminataMentreEriVia: false });
  },

  // Offer a draw
  offerDraw: () => {
    log.info('Multiplayer', 'offerDraw() called');
    const { roomId, status, drawOffered, opponentOfferedDraw, drawMustMove, drawOffersLeft } = get();
    if (status !== 'playing' || !roomId || drawOffered || opponentOfferedDraw) return;
    /* Il freno, anche qui: il server e' l'autorita', ma un giro di rete per
       farsi dire no e' un giro sprecato — e il bottone e' gia' spento. */
    if (drawMustMove || drawOffersLeft <= 0) {
      log.info('Multiplayer', 'offerDraw() bloccata dal freno', { drawMustMove, drawOffersLeft });
      return;
    }
    /* Lo stato di PRIMA, per poterlo rimettere se la proposta non parte: un
       ack d'errore senza `offertePatta` (rete caduta, ack scaduto, freno a
       raffica di un server piu' vecchio) lascerebbe altrimenti il blocco
       ottimistico addosso a un'offerta che il server non ha mai registrato —
       bottone spento con un motivo falso, che e' il difetto di 4d09615b. */
    const primaDellOfferta = { drawMustMove, drawOffersLeft };
    // A new offer clears the previous refusal, so the banner can't linger over
    // a proposal that is once again open.
    set({
      drawOffered: true,
      drawDeclinedNotice: false,
      /* Ottimistico, come `drawOffered`: l'ack arriva con il conto vero e
         sovrascrive. Serve perche' fra il gesto e l'ack il bottone non deve
         poter partire una seconda volta. */
      drawMustMove: true,
      drawOffersLeft: Math.max(0, drawOffersLeft - 1),
    });
    // Roll back the optimistic flag if the server rejects the offer (e.g. throttled),
    // otherwise the draw button stays stuck on "Draw sent" forever.
    void socketOfferDraw(roomId).then((res) => {
      /* Il conto del server vince sempre, riuscita o rifiuto: l'ack e' l'unica
         occasione in cui il client si riallinea. Se il campo non c'e' si tiene
         quel che si ha — mai spegnere il bottone per un campo assente. */
      if (res?.offertePatta) {
        set({
          drawOffersLeft: res.offertePatta.restanti,
          drawMustMove: res.offertePatta.deviMuovere,
        });
      }
      if (res?.error && get().drawOffered) {
        log.warn('Multiplayer', 'offerDraw rejected — rolling back flag', { error: res.error, code: res.code });
        /* Senza il conto del server si torna a com'era: l'offerta non e'
           partita, quindi non ha speso niente e non blocca niente. */
        if (!res.offertePatta) set(primaDellOfferta);
        /*
         * Chiave i18n, non la frase inglese dell'ack. `res.error` è scritta per i
         * log (`socket.ts` risolve sempre, mai rifiuta: `resolve({ error: … })`), e
         * questo campo lo rende `AvvisoErrore` — che traduce. Il gemello d'arena la
         * mappatura ce l'aveva già, con la nota che spiega perché la frase inglese
         * non deve finire in `error`.
         *
         * `chiaveRifiutoPatta` guarda prima i codici del FRENO (che hanno una
         * frase loro, condivisa dalle tre schermate), poi la mappa di questo
         * dominio, e solo alla fine ripiega su «non e' stato possibile»: una
         * frase precisa che esiste non va buttata via.
         */
        set({
          drawOffered: false,
          error: chiaveRifiutoPatta(res, CHIAVI_ERRORE_ONLINE, 'online.errors.drawOfferFailed'),
        });
      }
    }).catch((e) => log.error('Multiplayer', 'offerDraw promise rejected', e));
  },

  // Accept opponent's draw offer
  acceptDraw: () => {
    log.info('Multiplayer', 'acceptDraw() called');
    const { roomId, status, opponentOfferedDraw } = get();
    if (status !== 'playing' || !roomId || !opponentOfferedDraw) return;
    void socketAcceptDraw(roomId).then((risposta) => rispostaAllaPatta(roomId, risposta, set, get));
  },

  // Decline opponent's draw offer
  declineDraw: () => {
    log.info('Multiplayer', 'declineDraw() called');
    const { roomId, status, opponentOfferedDraw } = get();
    if (status !== 'playing' || !roomId || !opponentOfferedDraw) return;
    void socketDeclineDraw(roomId).then((risposta) => rispostaAllaPatta(roomId, risposta, set, get));
    set({ opponentOfferedDraw: false });
  },

  // Request a takeback to `toPly` (casual online only; opponent must consent).
  requestTakeback: (toPly: number) => {
    log.info('Multiplayer', 'requestTakeback() called', { toPly });
    const { roomId, status, isRated, takebackOffered, opponentRequestedTakeback } = get();
    if (status !== 'playing' || !roomId || isRated !== false) return;
    if (takebackOffered || opponentRequestedTakeback) return;
    set({ takebackOffered: true, takebackDeclinedNotice: false });
    // Roll back the optimistic flag if the server rejects the request.
    void socketRequestTakeback(roomId, toPly).then((res) => {
      if (res?.error && get().takebackOffered) {
        log.warn('Multiplayer', 'requestTakeback rejected — rolling back flag', { error: res.error });
        set({ takebackOffered: false });
      }
    }).catch((e) => log.error('Multiplayer', 'requestTakeback promise rejected', e));
  },

  // Accept opponent's takeback request (board rolls back via onTakebackAccepted).
  acceptTakeback: () => {
    log.info('Multiplayer', 'acceptTakeback() called');
    const { roomId, opponentRequestedTakeback } = get();
    if (!roomId || !opponentRequestedTakeback) return;
    socketAcceptTakeback(roomId);
  },

  // Decline opponent's takeback request
  declineTakeback: () => {
    log.info('Multiplayer', 'declineTakeback() called');
    const { roomId, opponentRequestedTakeback } = get();
    if (!roomId || !opponentRequestedTakeback) return;
    socketDeclineTakeback(roomId);
    set({ opponentRequestedTakeback: false, pendingTakebackPlies: null, pendingTakebackToPly: null });
  },

  // Request a rematch
  requestRematch: async () => {
    log.info('Multiplayer', 'requestRematch() called');
    const { roomId, status, canRematch } = get();
    if (status !== 'ended' || !roomId || !canRematch) {
      return;
    }

    // A fresh offer clears the previous outcome — otherwise "ha rifiutato"
    // would still be on screen under the new countdown.
    set({ isRequestingRematch: true, rematchNotice: null });

    try {
      const result = await socketRequestRematch(roomId);
      // If accepted, the rematch:start event will be received
      // If not accepted yet, we wait for opponent — with the server's own
      // deadline, so the countdown can't disagree with the timer that enforces
      // it. This same action also ACCEPTS the opponent's pending offer, and
      // there the ack carries no deadline (the game starts instead).
      if (!result.accepted) {
        set({ rematchOfferExpiresAt: result.expiresAt ?? null });
      }
    } catch (err) {
      log.error('Multiplayer', 'requestRematch failed', err);
      set({
        isRequestingRematch: false,
        rematchOfferExpiresAt: null,
        error: err instanceof Error ? err.message : 'Failed to request rematch',
      });
    }
  },

  // Decline a rematch
  declineRematch: async () => {
    const { roomId, status } = get();
    if (status !== 'ended' || !roomId) {
      return;
    }

    try {
      await socketDeclineRematch(roomId);
      trackRematchDeclined();
      // No notice: this player is the one who refused, they know.
      set({
        ...REMATCH_OFFER_RESET,
        canRematch: false,
      });
    } catch (err) {
      log.error('Multiplayer', 'declineRematch failed', err);
      set({
        error: err instanceof Error ? err.message : 'Failed to decline rematch',
      });
    }
  },

  /**
   * Signal readiness for the return leg. When both players do, the server skips
   * the rest of the countdown and starts the leg immediately.
   */
  setReturnLegReady: () => {
    const { roomId, returnLegStartsAt, returnLegReady } = get();
    if (!roomId || returnLegStartsAt === null || returnLegReady) return;
    set({ returnLegReady: true });
    socketSetMatchReady(roomId);
  },

  // Reset state for new game
  reset: () => {
    const currentRoomId = get().roomId;
    log.info('Multiplayer', 'reset() called', { wasStatus: get().status, roomId: currentRoomId });
    // Notify server that player is leaving the game room
    if (currentRoomId && isConnected()) {
      const socket = getSocket();
      if (socket) {
        log.info('Multiplayer', 'reset → emit game:leave', { roomId: currentRoomId });
        socket.emit('game:leave', currentRoomId);
      }
    }

    // Leaving the game ends the conversation: transcript, unread badge and the
    // session mute all go with it.
    useChatStore.getState().clearChatContext();

    set({
      status: isConnected() ? 'connected' : 'disconnected',
      roomId: null,
      playerColor: null,
      /*
       * `variant` NON si azzera qui, per la stessa ragione di `isRated` poco
       * piu' sotto: la rivincita riusa la stessa stanza del server e arriva per
       * `rematch:start`, non per `game:start`. Mettere un default al suo posto
       * voleva dire ASSERIRE una variante che non si conosce — ed e' cosi' che
       * chi usciva dalla schermata di fine partita e poi accettava l'invito
       * ritrovava «Internazionale» su una damiera italiana — col punteggio preso
       * dal bucket sbagliato. Un valore vecchio non puo' filtrare: ogni
       * inizio partita lo riscrive, e i due lettori preferiscono comunque
       * `boardState.variant`.
       */
      error: null,
      boardState: null,
      preMoveBoardState: null,
      preMoveSentAt: null,
      lastMove: null,
      gameResult: null,
      gameEndReason: null,
      gameStartedAtMs: null,
      primaMossaScadeAt: null,
      primaMossaEsito: null,
      openingPlyCount: 0,
      /* Si lascia la partita: la sua posizione di partenza non segue. E' la
         porta che tiene pulito l'ingresso in una partita NORMALE. */
      startBoard: null,
      clockState: null,
      timeControl: null,
      // NOTE: `isRated` is intentionally NOT reset here. A rematch reuses the same
      // server room (rematch:start, not game:start), so a player who left the
      // game-over screen and re-accepts a global rematch invite must keep the
      // original game's ratedness. onGameStart overwrites it for every genuinely
      // new game, so preserving it across reset() can never leak a stale value.
      searchTimeControl: null,
      searchStartTime: null,
      queuePosition: null,
      estimatedWait: null,
      opponent: null,
      drawnOpening: null,
      sessionScore: { white: 0, black: 0 },
      ...REMATCH_OFFER_RESET,
      canRematch: false,
      ...MATCH_RESET,
      drawOffered: false,
      ...PATTA_BUDGET_RESET,
      opponentOfferedDraw: false,
      drawDeclinedNotice: false,
      ...TAKEBACK_RESET,
    });
  },

  // Set variant
  setVariant: (variant: GameVariant) => {
    set({ variant });
  },

  // ========== Clock Actions ==========

  // Initialize clock from game:start event
  initClock: (clockInit: GameClockInit) => {
    if (!clockInit || clockInit.timeControl === 'none') {
      set({ clockState: null, timeControl: null });
      return;
    }

    // activePlayer is null until first move - clock only starts ticking
    // when server sends game:clock-sync with non-null activePlayer
    const newClockState = {
      white: clockInit.white,
      black: clockInit.black,
      activePlayer: null, // Clock doesn't tick until first move
      lastSyncAt: Date.now(),
      increment: clockInit.increment,
    };
    set({
      clockState: newClockState,
      timeControl: clockInit.timeControl,
    });
  },

  // Sync clock from server game:clock-sync event
  syncClock: (sync: ClockSyncEvent) => {
    const { status, isOpponentDisconnected } = get();
    if (status !== 'playing') {
      log.warn('Multiplayer', 'syncClock no-op (status != playing)', { status });
      return;
    }

    set({
      clockState: {
        white: sync.white,
        black: sync.black,
        activePlayer: sync.activePlayer,
        lastSyncAt: Date.now(),
        increment: get().clockState?.increment ?? 0,
      },
    });
  },

  // Local tick for smooth clock interpolation (call every ~100ms)
  tickClock: () => {
    const { clockState, status } = get();
    if (!clockState || status !== 'playing' || !clockState.activePlayer || !clockState.lastSyncAt) {
      log.warn('Multiplayer', 'tickClock no-op', { status, hasClock: !!clockState, activePlayer: clockState?.activePlayer ?? null });
      return;
    }

    const elapsed = Date.now() - clockState.lastSyncAt;
    const activePlayer = clockState.activePlayer;

    // Deduct elapsed time from active player
    const newTime = Math.max(0, clockState[activePlayer] - elapsed);
    if (newTime === 0) {
      log.info('Multiplayer', 'tickClock local clock reached 0 (low-expiry)', { activePlayer });
    }

    set({
      clockState: {
        ...clockState,
        [activePlayer]: newTime,
        lastSyncAt: Date.now(),
      },
    });
  },

  // Internal setters
  setStatus: (status) => { log.info('Multiplayer', `status → ${status}`); set({ status }); },
  setError: (error) => set({ error }),
  setBoardState: (boardState) => set({ boardState }),
  setGameEnd: (result, reason) => set({
    status: 'ended',
    gameResult: result,
    gameEndReason: reason,
    clockState: null, // Clear clock on game end
    drawOffered: false,
    ...PATTA_BUDGET_RESET,
    opponentOfferedDraw: false,
    drawDeclinedNotice: false,
    ...TAKEBACK_RESET,
  }),
}));
